import * as React from 'react';
import { Rectangle } from 'ark-common';
import {
  Point, NamedPoint, PointName, unnamedPoint, scalePoint, Line, LineStrip, LineType, LineCaps,
  DrawingMode, Tiling, ShapeKind, ShapeKindGroupFigure, DrawingShape, FigureShape, NoteShape,
  BalloonShape, StraightLineShape, ImageFileShape, FontType, Offset, isPointEqual,
  getRectCoveringAllPoints, Rect, TransformDelta, emptyTransformDelta, getAxisAlignedLineFromPoints,
  MultiVertexShape, PolylineShape, PolygonShape, StickyShape, MultiroleLinkShape, Stamp, StampShape,
} from '../types';
import {
  MultiroleLinkKind, MultiroleLinkInfo, createMultiroleLinkInfoFromMultiroleLinkCreateInfo,
} from '../../../../../common/src/ts/types/multiroleLink';
import { getClientPoint } from '../util/getClientPoint';
import { drawLineStrip } from '../picture';
import { UIEvent } from '../../uievent';
import {
  DrawingAction, replaceHandwritingTiles, addFigure, addSticky, addBalloon, addStraightLine,
  addPolyline, addPolygon, pasteImage, pasteMultiroleLink, activateLayer, moveLayer, resizeLayer,
  removeLayer, ImageEntry, movePoint, AddFigureAction, foldNote, addStamp,
  toggleTransparencyOfLearningPen,
} from '../actions/DrawingAction';
import {
  DrawingState,
  bottomLayerID,
  moveMatchingVertex,
  transformVertices,
  moveStraightLineShape,
  moveStraightLineShapeVertex,
  isReadyToPaint,
} from '../reducers/drawing';
import {
  imageTileSize,
  fundamentalSizeMultiplier,
  textBoxDefaultPaddingSize,
} from '../constants';
import BlobImageLoader from '../util/BlobImageLoader';
import { generateAttachmentID } from '../../helpers/attachments';
import {
  DrawingToolState, DrawingAttr, PastingImage, PastingImageState,
  defaultLineCaps, solidRoundLineCaps, StampImageState,
} from '../reducers/drawingTool';
import getTilingFromLine from '../util/getTilingFromLine';
import TextBox from '../components/TextBox';
import ClickableArea from '../components/ClickableArea';
import { hasPointerEvents } from '../../../../../common/src/ts/utils/uievent';
import { ViewTransformSelectorProps } from '../../selectors/zoomingSelector';
import { PictureStorageState } from '../reducers/pictureStorage';

export interface DrawingCanvasArrangementProps {
  scale: number;
  translateX: number;
  translateY: number;
  fundamentalWidth: number;
  fundamentalHeight: number;
  pageAreaWidth: number;
  pageAreaHeight: number;
}

interface DrawingCanvasProps extends DrawingCanvasArrangementProps {
  stamps: Stamp[];
  toolState: DrawingToolState;
  data: DrawingState;
  pictureStorage: PictureStorageState<HTMLCanvasElement>;
  margin: number;
  focus: boolean;
  onSetPastingImages: (pastingImages: ReadonlyArray<PastingImage>) => void;
  onDrawingAction: (action: DrawingAction) => void;
  onMultiroleLinkClick: (multiroleLink: MultiroleLinkInfo) => void;
  onRequestFocus?: () => void;
}

type DrawingCanvasPropsType = DrawingCanvasProps & ViewTransformSelectorProps;

interface DrawingStateDisabled {
  readonly mode: DrawingMode.Disabled;
}

interface DrawingStateSelection {
  readonly mode: DrawingMode.Selection;
  readonly offset: Offset;
  readonly activateLayerExecuted: boolean;
}

interface DrawingStateResizing {
  readonly mode: DrawingMode.Resizing;
  readonly offset: Offset;
}

interface DrawingStateBalloonArrowMoving {
  readonly mode: DrawingMode.BalloonArrowMoving;
  readonly offset: Offset;
}

interface DrawingStatePointMoving {
  readonly mode: DrawingMode.PointMoving;
  readonly offset: Offset;
}

interface DrawingStateErasing {
  readonly mode: DrawingMode.ObjectRemoval;
  readonly hover: boolean;
}

interface DrawingStateStroke {
  readonly mode: DrawingMode.Handwriting | DrawingMode.Handerasing |
    DrawingMode.VerticalStraightLineHandWriting | DrawingMode.HorizontalStraightLineHandWriting;
  readonly vertices: LineStrip;
  readonly modifiedTiles: Tiling<HTMLCanvasElement>;
  // VerticalStraightLineHandWriting, HorizontalStraightLineHandWriting時のみ最初のxまたはyを維持するため
  readonly begin: Point | null;
}

interface DrawingStateObject {
  readonly mode: DrawingMode.RectDrawing | DrawingMode.SquareDrawing |
  DrawingMode.OvalDrawing | DrawingMode.CircleDrawing |
  DrawingMode.TriangleDrawing | DrawingMode.EquilateralTriangleDrawing |
  DrawingMode.StickyDrawing | DrawingMode.BalloonDrawing | DrawingMode.Stamp |
  DrawingMode.LearningPenRect;
  readonly start: Point;
  readonly end: Point;
}

interface DrawingStateStraightLine {
  readonly mode: DrawingMode.StraightLineDrawing | DrawingMode.AxisAlignedStraightLineDrawing;
  readonly begin: Point;
  readonly end: Point;
}

interface DrawingStateMultiVertex {
  readonly mode: DrawingMode.MultiVertexDrawing;
  readonly vertices: LineStrip;
  readonly currentPos: Point;
}

interface DrawingStateImageFile {
  readonly mode: DrawingMode.ImagePasting;
  readonly pos: Point;
}

interface DrawingStateMultiroleLink {
  readonly mode: DrawingMode.MultiroleLinkPasting;
  readonly pos: Point;
}

type DrawingStateWorkingData = DrawingStateDisabled | DrawingStateSelection | DrawingStateStroke
  | DrawingStateObject | DrawingStateStraightLine | DrawingStateMultiVertex | DrawingStateImageFile
  | DrawingStateMultiroleLink | DrawingStateResizing | DrawingStateErasing
  | DrawingStateBalloonArrowMoving | DrawingStatePointMoving;

interface DrawingCanvasState {
  work: DrawingStateWorkingData;
  editingText: boolean;
}

export enum PaintMode {
  Normal,
  Visible,
  Folded,
}

function isTransformResizeOrMove(delta: TransformDelta): boolean {
  switch (delta.origin.name) {
    case PointName.ArrowTip:
    case PointName.PolygonVertex:
    case PointName.LineBegin:
    case PointName.LineEnd:
      return false;
    default:
      return true;
  }
}

function doseTransformMoveBalloonArrow(delta: TransformDelta): boolean {
  switch (delta.origin.name) {
    case PointName.ArrowTip:
    case PointName.None:
      return true;
    default:
      return false;
  }
}

function getRectFromPoints(start: Point, end: Point): Rect {
  return {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(start.x - end.x),
    height: Math.abs(start.y - end.y),
  };
}

function getSquareRectFromPoints(start: Point, end: Point): Rect {
  const dx = (end.x - start.x);
  const dy = (end.y - start.y);
  const dmin = Math.min(Math.abs(dx), Math.abs(dy));
  return {
    x: start.x + dmin * (dx < 0 ? -1 : 0),
    y: start.y + dmin * (dy < 0 ? -1 : 0),
    width: dmin,
    height: dmin,
  };
}

function getLearningPenRect(start: Point, end: Point, width: number): Rect {
  // 直線の座標を取得
  const alignedLine = getAxisAlignedLineFromPoints(start, end);
  const isVertical = alignedLine.begin.x === alignedLine.end.x;
  const halfWidth = width / 2;

  const alignedSquareBegin: Point = {
    x: alignedLine.begin.x - (isVertical ? halfWidth : 0),
    y: alignedLine.begin.y - (isVertical ? 0 : halfWidth),
  };

  const alignedSquareEnd: Point = {
    x: alignedLine.end.x + (isVertical ? halfWidth : 0),
    y: alignedLine.end.y + (isVertical ? 0 : halfWidth),
  };

  return getRectFromPoints(alignedSquareBegin, alignedSquareEnd);
}

function getStampRectFromSizeAndPoints(width: number,
                                       height: number,
                                       start: Point,
                                       end: Point): Rect {
  const startPoint = { x: start.x - width / 2, y: start.y - height / 2 };
  const endPoint = { x: end.x + width / 2, y: end.y + height / 2 };
  if (start.x === end.x && start.y === end.y) {
    return {
      width,
      height,
      x: startPoint.x,
      y: startPoint.y,
    };
  }

  return getSquareRectFromPoints(startPoint, endPoint);
}

function getEquilateralTriangleRectFromPoints(start: Point, end: Point): Rect {
  const rect = getSquareRectFromPoints(start, end);
  const { x, y, width, height } = rect;
  const half = width / 2;
  const triangleHeight = Math.sqrt(Math.abs(rect.height * rect.height - half * half));
  return { x, width, y: y + (height - triangleHeight), height: triangleHeight };
}

function getRectFromCenterPoint(center: Point, rx: number, ry: number): Rect {
  return {
    x: center.x - rx,
    y: center.y - ry,
    width: rx * 2,
    height: ry * 2,
  };
}

function getCenterPointFromRect(rect: Rect): Point {
  const { x, y, width, height } = rect;
  return { x: x + width / 2, y: y + height / 2 };
}

function getInflatedRect(rect: Rect, size: number): Rect {
  return {
    x: rect.x - size,
    y: rect.y - size,
    width: rect.width + size * 2,
    height: rect.height + size * 2,
  };
}

function transformRect(rect: Rect, delta: TransformDelta): Rect {
  return {
    x: rect.x + delta.x,
    y: rect.y + delta.y,
    width: rect.width + delta.width,
    height: rect.height + delta.height,
  };
}

function isPointInRect(pos: Point, rect: Rect): boolean {
  const { x, y, width, height } = rect;
  return ((x <= pos.x && pos.x < x + width) && (y <= pos.y && pos.y < y + height));
}

function getMiddlePointsFromRect(rect: Rect): NamedPoint[] {
  const { x, y, width, height } = rect;
  const wHalf = width / 2;
  const hHalf = height / 2;
  return [
    { y, x: x + wHalf, name: PointName.TopCenter },
    { x, y: y + hHalf, name: PointName.MiddleLeft },
    { x: x + width, y: y + hHalf, name: PointName.MiddleRight },
    { x: x + wHalf, y: y + height, name: PointName.BottomCenter },
  ];
}

function getCornerPointsFromRect(rect: Rect): NamedPoint[] {
  const { x, y, width, height } = rect;
  return [
    { x, y, name: PointName.TopLeft },
    { y, x: x + width, name: PointName.TopRight },
    { x, y: y + height, name: PointName.BottomLeft },
    { x: x + width, y: y + height, name: PointName.BottomRight },
  ];
}

// https://en.wikipedia.org/wiki/Line%E2%80%93line_intersection
function getIntersectionPointOfLineAndLine(l1: Line, l2: Line): Point {
  const x1 = l1.begin.x;
  const y1 = l1.begin.y;
  const x2 = l1.end.x;
  const y2 = l1.end.y;
  const x3 = l2.begin.x;
  const y3 = l2.begin.y;
  const x4 = l2.end.x;
  const y4 = l2.end.y;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (denom === 0) {
    return {
      x: Number.POSITIVE_INFINITY,
      y: Number.POSITIVE_INFINITY,
    };
  }

  return {
    x: ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / denom,
    y: ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / denom,
  };
}

function getIntersectionPointOfRectAndLine(rect: Rect, line: Line): Point[] {
  const { x, y, width, height } = rect;
  const rectLines = [
    { begin: { x, y }, end: { y, x: x + width } }, // top
    { begin: { x, y: y + height }, end: { x: x + width, y: y + height } }, // bottom
    { begin: { x, y }, end: { x, y: y + height } }, // left
    { begin: { y, x: x + width }, end: { x: x + width, y: y + height } }, // right
  ];
  const points: Point[] = [];

  for (const rectLine of rectLines) {
    const intersectionPoint = getIntersectionPointOfLineAndLine(line, rectLine);
    // 誤差によりrect外になることがあったので0.5だけinflateしている TODO:誤差が少ない or 出ない計算
    if (isPointInRect(intersectionPoint, getInflatedRect(rect, 0.5))) {
      if (points.every((point: Point) => !isPointEqual(point, intersectionPoint))) {
        points.push(intersectionPoint);
      }
    }
  }

  return points;
}

function findClosestPoint(queryPoint: Point, sourcePoints: Point[]): Point | null {
  let minDistance = Number.POSITIVE_INFINITY;
  let closestPoint: Point | null = null;

  for (const point of sourcePoints) {
    const dx = point.x - queryPoint.x;
    const dy = point.y - queryPoint.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < minDistance) {
      minDistance = distance;
      closestPoint = point;
    }
  }

  return closestPoint;
}

// equals to convertLineWidthToValue(LineWidth.one);
// TODO: Improve line dash width limiting mechanism.
const lineDashWidthLowerLimit = 1 * fundamentalSizeMultiplier;

function setLineDash(context: CanvasRenderingContext2D, lineType: LineType, lineWidth: number) {
  switch (lineType) {
    case LineType.Solid:
      context.setLineDash([]);
      break;
    case LineType.Dotted:
      context.setLineDash([lineWidth, lineWidth]);
      break;
    case LineType.Dashed:
      // 細い線だと拡大率によって破線に見えにくいので調整をいれている
      if (lineWidth === lineDashWidthLowerLimit) {
        context.setLineDash([lineWidth * 3, lineWidth * 3]);
      } else {
        context.setLineDash([lineWidth * 2, lineWidth]);
      }
      break;
    case LineType.None:
      context.setLineDash([]);
      break;
  }
}

function drawRect(
  context: CanvasRenderingContext2D,
  rect: Rect,
  fillColor: string,
  lineColor: string,
  lineWidth: number,
  lineType: LineType,
  lineCaps: LineCaps,
  alpha: number,
  isFilled: boolean,
  opaqueLine: boolean,
) {
  context.save();
  context.globalAlpha = alpha;
  context.lineWidth = lineWidth;
  setLineDash(context, lineType, lineWidth);
  context.lineCap = lineCaps[lineType];
  if (isFilled) {
    context.fillStyle = fillColor;
    context.fillRect(rect.x, rect.y, rect.width, rect.height);
  }
  if (opaqueLine) {
    context.globalAlpha = 1;
  }
  context.strokeStyle = lineColor;
  context.strokeRect(rect.x, rect.y, rect.width, rect.height);
  context.restore();
}

function drawStamp(
  context: CanvasRenderingContext2D,
  rect: Rect,
  entry: ImageEntry,
) {
  const { picture } = entry;
  context.save();
  context.drawImage(picture, rect.x, rect.y, rect.width, rect.height);
  context.restore();
}

function drawSelectionMark(context: CanvasRenderingContext2D, point: Point, scale: number) {
  // 拡縮に関わらず一定のサイズで描きたいので、描画全体に適用される scale であらかじめ割っておく
  const half = 4 / scale;
  const borderWidth = 1 / scale;

  const rect = getRectFromCenterPoint(point, half, half);
  drawRect(
    context, rect,
    '#fff', '#000',
    borderWidth, LineType.Solid,
    defaultLineCaps, 1, true, false,
  );
}

function getDefaultBalloonArrow(rect: Rect) {
  const { x, y, width, height } = rect;
  const offsetW = width / 8;
  const offsetH = height / 8;
  return { x: x - offsetW, y: y + height + offsetH }; // TODO:適当な値を探す
}

function drawBalloon(
  context: CanvasRenderingContext2D,
  rect: Rect,
  fillColor: string,
  lineColor: string,
  lineWidth: number,
  lineType: LineType,
  lineCaps: LineCaps,
  alpha: number,
  isFilled: boolean,
  arrowPoint: Point,
) {
  context.save();
  context.globalAlpha = alpha;
  context.lineWidth = lineWidth;
  setLineDash(context, lineType, lineWidth);
  context.lineCap = lineCaps[lineType];

  // Conetsのを真似た吹き出し
  // TODO:ものすごくベタがき、かつ、カニみたいでカッコよくないなのでなんかしら新しいやつ考えた方がよさそう
  const { x, y, width, height } = rect;
  const radius = Math.abs(Math.min(width, height)) / 10; // TODO:すごく適当なのでいい感じのradiusを算出する
  const halfW = width / 2;
  const halfH = height / 2;
  let arrowDrawn = false;
  context.beginPath();
  context.moveTo(x + halfW, y);
  context.lineTo(x + halfW + halfW * 0.75, y);
  if (arrowPoint.x > x + halfW && arrowPoint.x < x + width && arrowPoint.y < y) {
    context.lineTo(arrowPoint.x, arrowPoint.y);
    context.lineTo(x + width, y);
    arrowDrawn = true;
  } else if (arrowPoint.x > x + width && arrowPoint.y < y) {
    context.lineTo(arrowPoint.x, arrowPoint.y);
    arrowDrawn = true;
  } else if (arrowPoint.x > x + width && arrowPoint.y > y && arrowPoint.y < y + halfH) {
    context.lineTo(x + width, y);
    context.lineTo(arrowPoint.x, arrowPoint.y);
    arrowDrawn = true;
  } else {
    context.lineTo(x + width - radius, y);
    context.arc(x + width - radius, y + radius, radius, Math.PI * 1.5, 0, false);
  }

  context.lineTo(x + width, y + halfH * 0.25);
  context.lineTo(x + width, y + halfH);
  context.lineTo(x + width, y + halfH + halfH * 0.75);
  if (!arrowDrawn) {
    if (arrowPoint.y > y + halfH && arrowPoint.y < y + height && arrowPoint.x > x + width) {
      context.lineTo(arrowPoint.x, arrowPoint.y);
      context.lineTo(x + width, y + height);
      arrowDrawn = true;
    } else if (arrowPoint.y > y + height && arrowPoint.x > x + width) {
      context.lineTo(arrowPoint.x, arrowPoint.y);
      arrowDrawn = true;
    } else if (arrowPoint.y > y + height && arrowPoint.x > x + halfW && arrowPoint.x < x + width) {
      context.lineTo(x + width, y + height);
      context.lineTo(arrowPoint.x, arrowPoint.y);
      arrowDrawn = true;
    } else {
      context.lineTo(x + width, y + height - radius);
      context.arc(x + width - radius, y + height - radius, radius, 0, Math.PI * 0.5, false);
    }
  } else {
    context.lineTo(x + width, y + height - radius);
    context.arc(x + width - radius, y + height - radius, radius, 0, Math.PI * 0.5, false);
  }

  context.lineTo(x + halfW + halfW * 0.75, y + height);
  context.lineTo(x + halfW, y + height);
  context.lineTo(x + halfW * 0.25, y + height);
  if (!arrowDrawn) {
    if (arrowPoint.x > x && arrowPoint.x < x + halfW && arrowPoint.y > y + height) {
      context.lineTo(arrowPoint.x, arrowPoint.y);
      context.lineTo(x, y + height);
      arrowDrawn = true;
    } else if (arrowPoint.x < x && arrowPoint.y > y + height) {
      context.lineTo(arrowPoint.x, arrowPoint.y);
      arrowDrawn = true;
    } else if (arrowPoint.x < x && arrowPoint.y > y + halfH && arrowPoint.y < y + height) {
      context.lineTo(x, y + height);
      context.lineTo(arrowPoint.x, arrowPoint.y);
      arrowDrawn = true;
    } else {
      context.lineTo(x + radius, y + height);
      context.arc(x + radius, y + height - radius, radius, Math.PI * 0.5, Math.PI, false);
    }
  } else {
    context.lineTo(x + radius, y + height);
    context.arc(x + radius, y + height - radius, radius, Math.PI * 0.5, Math.PI, false);
  }

  context.lineTo(x, y + halfH + halfH * 0.75);
  context.lineTo(x, y + halfH);
  context.lineTo(x, y + halfH * 0.25);
  if (!arrowDrawn) {
    if (arrowPoint.x < x && arrowPoint.y > y && arrowPoint.y < y + halfH) {
      context.lineTo(arrowPoint.x, arrowPoint.y);
      context.lineTo(x, y);
      arrowDrawn = true;
    } else if (arrowPoint.x < x && arrowPoint.y < y) {
      context.lineTo(arrowPoint.x, arrowPoint.y);
      arrowDrawn = true;
    } else if (arrowPoint.x > x && arrowPoint.x < x + halfW && arrowPoint.y < y) {
      context.lineTo(x, y);
      context.lineTo(arrowPoint.x, arrowPoint.y);
      arrowDrawn = true;
    } else {
      context.lineTo(x, y + radius);
      context.arc(x + radius, y + radius, radius, Math.PI, Math.PI * 1.5, false);
    }
  } else {
    context.lineTo(x, y + radius);
    context.arc(x + radius, y + radius, radius, Math.PI, Math.PI * 1.5, false);
  }
  context.lineTo(x + halfW * 0.25, y);
  context.lineTo(x + halfW, y);
  context.closePath();

  if (isFilled) {
    context.fillStyle = fillColor;
    context.fill();
  }
  context.globalAlpha = 1;
  context.strokeStyle = lineColor;
  context.stroke();
  context.restore();
}

// TODO: TextBoxとの微妙な位置合わせ
function multilineText(
  context: CanvasRenderingContext2D,
  text: string,
  width: number,
) {
  const len = text.length;
  const strArray: string[] = [];
  let tmp = '';

  if (len < 1) {
    // textの文字数が0だったらから文字列を入れて終わり
    strArray.push(tmp);
    return strArray;
  }

  for (let i = 0; i < len; i += 1) {
    // textから１文字抽出
    const c = text.charAt(i);
    if (c === '\n') {
      /* 改行コードの場合はそれまでの文字列を配列にセット */
      strArray.push(tmp);
      tmp = '';

      continue;
    }

    /* contextの現在のフォントスタイルで描画したときの長さを取得 */
    if (context.measureText(tmp + c).width <= width) {
      /* 指定幅を超えるまでは文字列を繋げていく */
      tmp += c;
    } else {
      /* 超えたら、それまでの文字列を配列にセット */
      strArray.push(tmp);
      tmp = c;
    }
  }

  /* 繋げたままの分があれば回収 */
  if (tmp.length > 0) {
    strArray.push(tmp);
  }

  return strArray;
}

function fillMultilineText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  width: number,
  lineHeight: number,
) {
  const lines = multilineText(context, text, width);
  const linesLen = lines.length;
  for (let i = 0; i < linesLen; i += 1) {
    context.fillText(lines[i], x, y + lineHeight * i);
  }
}

function setOvalPath(context: CanvasRenderingContext2D, rect: Rect) {
  const rx: number = rect.width / 2;
  const ry: number = rect.height / 2;
  const cx: number = rect.x + rx;
  const cy: number = rect.y + ry;

  context.save();
  context.beginPath();

  context.translate(cx - rx, cy - ry);
  context.scale(rx, ry);
  context.arc(1, 1, 1, 0, 2 * Math.PI, false);

  context.restore();
}

function drawOval(
  context: CanvasRenderingContext2D,
  rect: Rect,
  fillColor: string,
  lineColor: string,
  lineWidth: number,
  lineType: LineType,
  lineCaps: LineCaps,
  alpha: number,
  isFilled: boolean,
) {
  context.save();
  context.globalAlpha = alpha;
  context.lineWidth = lineWidth;
  setLineDash(context, lineType, lineWidth);
  context.lineCap = lineCaps[lineType];
  context.strokeStyle = lineColor;
  setOvalPath(context, rect);
  if (isFilled) {
    context.fillStyle = fillColor;
    context.fill();
  }
  context.stroke();
  context.restore();
}

function setTrianglePath(context: CanvasRenderingContext2D, rect: Rect) {
  context.beginPath();

  context.moveTo(rect.x + rect.width / 2, rect.y);
  context.lineTo(rect.x, rect.y + rect.height);
  context.lineTo(rect.x + rect.width, rect.y + rect.height);

  context.closePath();
}

function drawTriangle(
  context: CanvasRenderingContext2D,
  rect: Rect,
  fillColor: string,
  lineColor: string,
  lineWidth: number,
  lineType: LineType,
  lineCaps: LineCaps,
  alpha: number,
  isFilled: boolean,
) {
  context.save();
  context.globalAlpha = alpha;
  setTrianglePath(context, rect);
  if (isFilled) {
    context.fillStyle = fillColor;
    context.fill();
  }
  context.lineWidth = lineWidth;
  setLineDash(context, lineType, lineWidth);
  context.lineCap = lineCaps[lineType];
  context.strokeStyle = lineColor;
  context.stroke();
  context.restore();
}

function getClickableLineWidth(lineWidth: number) {
  return Math.max(lineWidth, 20); // TODO:適切な値を探す
}

function drawLine(
  context: CanvasRenderingContext2D,
  line: Line,
  lineColor: string,
  lineWidth: number,
  lineType: LineType,
  lineCaps: LineCaps,
  alpha: number,
) {
  context.save();
  context.globalAlpha = alpha;
  context.lineWidth = lineWidth;
  setLineDash(context, lineType, lineWidth);
  context.lineCap = lineCaps[lineType];
  context.strokeStyle = lineColor;
  context.beginPath();
  context.moveTo(line.begin.x, line.begin.y);
  context.lineTo(line.end.x, line.end.y);
  context.stroke();
  context.restore();
}

function setPolygonPath(context: CanvasRenderingContext2D, vertices: ReadonlyArray<Point>) {
  context.beginPath();
  context.moveTo(vertices[0].x, vertices[0].y);
  for (let index = 1; index < vertices.length; index += 1) {
    context.lineTo(vertices[index].x, vertices[index].y);
  }
  context.closePath();
}

function drawPolygon(
  context: CanvasRenderingContext2D,
  vertices: ReadonlyArray<Point>,
  fillColor: string,
  lineColor: string,
  lineWidth: number,
  lineType: LineType,
  lineCaps: LineCaps,
  alpha: number,
  isFilled: boolean,
) {
  context.save();
  context.globalAlpha = alpha;
  context.lineWidth = lineWidth;
  setLineDash(context, lineType, lineWidth);
  context.lineCap = lineCaps[lineType];
  context.strokeStyle = lineColor;
  setPolygonPath(context, vertices);
  if (isFilled) {
    context.fillStyle = fillColor;
    context.fill();
  }
  context.stroke();
  context.restore();
}

function setLinePathAsRect(
  context: CanvasRenderingContext2D, vertices: LineStrip, lineWidth: number,
) {
  if (vertices.length < 2) {
    throw new Error('LineStrip must have more than one vertex');
  }

  context.beginPath();
  for (let index = 0; index < vertices.length - 1; index += 1) {
    const begin = vertices[index];
    const end = vertices[index + 1];
    const dx = end.x - begin.x;
    const dy = end.y - begin.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length === 0) {
      continue;
    }

    const margin = lineWidth / 2;
    const shiftx = -dy * margin / length;
    const shifty = dx * margin / length;
    context.moveTo(begin.x + shiftx, begin.y + shifty);
    context.lineTo(begin.x - shiftx, begin.y - shifty);
    context.lineTo(end.x - shiftx, end.y - shifty);
    context.lineTo(end.x + shiftx, end.y + shifty);
  }
}

function setPolylinePath(context: CanvasRenderingContext2D, vertices: LineStrip) {
  context.beginPath();
  context.moveTo(vertices[0].x, vertices[0].y);
  for (let index = 1; index < vertices.length; index += 1) {
    context.lineTo(vertices[index].x, vertices[index].y);
  }
}

function drawPolyline(
  context: CanvasRenderingContext2D,
  vertices: LineStrip,
  lineColor: string,
  lineWidth: number,
  lineType: LineType,
  lineCaps: LineCaps,
  alpha: number,
) {
  context.save();
  context.globalAlpha = alpha;
  context.lineWidth = lineWidth;
  setLineDash(context, lineType, lineWidth);
  context.lineCap = lineCaps[lineType];
  context.strokeStyle = lineColor;
  setPolylinePath(context, vertices);
  context.stroke();
  context.restore();
}

function drawTemporaryPolyline(
  context: CanvasRenderingContext2D,
  vertices: LineStrip,
  scale: number,
) {
  context.save();
  setLineStyleOfGuide(context, scale);
  setPolylinePath(context, vertices);
  context.stroke();
  context.restore();
}

function setLineStyleOfGuide(
  context: CanvasRenderingContext2D, scale: number,
) {
  // 拡縮に関わらず一定の太さで描きたいので、描画全体に適用される scale であらかじめ割っておく
  context.lineWidth = 1 / scale;
  context.strokeStyle = '#000';
  context.setLineDash([5 * fundamentalSizeMultiplier, 10 * fundamentalSizeMultiplier]);
}

interface DrawingAttrAndAlpha extends DrawingAttr {
  readonly alpha: number;
}

function isVisbleDrawingAttr(attr: DrawingAttrAndAlpha) {
  return !(attr.fillColor === 'transparent' && attr.lineColor === 'transparent');
}

function getVisibleDrawingAttr(attr: DrawingAttrAndAlpha) {
  let { fillColor, lineColor, alpha, isFilled } = attr;
  // TODO: 見えなくなるパターンについて考える たとえばユーザ定義色でも透明になりえる
  // TODO: 一時的に見えるようにする色などが仮決めの適当なものなので検討する
  const isTransparent = !isVisbleDrawingAttr(attr);
  alpha = isTransparent ? 0.1 : alpha;
  fillColor = isTransparent ? 'yellow' : fillColor;
  lineColor = isTransparent ? 'black' : lineColor;
  isFilled = isTransparent ? true : isFilled;
  return { ...attr, alpha, fillColor, lineColor, isFilled };
}

abstract class ShapeHandler {
  public abstract paint(context: CanvasRenderingContext2D, delta: TransformDelta): void;
  public abstract getEnclosingRect(): Rect;

  public clipsPainting() {
    return true;
  }

  public isLayerDrawable() {
    return true;
  }

  public clipShapeRegion(context: CanvasRenderingContext2D, delta: TransformDelta) {
    this.setShapePath(context, delta);
    context.clip();
  }

  public paintSelectionFrame(
    context: CanvasRenderingContext2D, delta: TransformDelta, scale: number,
  ) {
    const rect = this.getTransformedRect(delta);
    context.save();
    setLineStyleOfGuide(context, scale);
    context.strokeRect(rect.x, rect.y, rect.width, rect.height);
    context.restore();
  }

  public paintSelectionMark(
    context: CanvasRenderingContext2D, delta: TransformDelta, scale: number,
  ) {
    const positions = this.getSelectionMarkPositions(delta);
    for (const pos of positions) {
      drawSelectionMark(context, pos, scale);
    }
  }

  public getSelectionMarkPositions(delta: TransformDelta = emptyTransformDelta): NamedPoint[] {
    const rect = this.getTransformedRect(delta);
    return getCornerPointsFromRect(rect).concat(getMiddlePointsFromRect(rect));
  }

  public isPointInLayer(pos: Point) {
    return isPointInRect(pos, this.getEnclosingRect());
  }

  public isPointInShape(pos: Point, _context: CanvasRenderingContext2D) {
    return this.isPointInLayer(pos);
  }

  public setShapePath(
    context: CanvasRenderingContext2D, delta: TransformDelta = emptyTransformDelta,
  ) {
    const rect = this.getTransformedRect(delta);
    context.beginPath();
    context.rect(rect.x, rect.y, rect.width, rect.height);
    context.closePath();
  }

  public getTransformedRect(delta: TransformDelta = emptyTransformDelta): Rect {
    return isTransformResizeOrMove(delta)
      ? transformRect(this.getEnclosingRect(), delta)
      : this.getEnclosingRect();
  }
}

class NullShapeHandler extends ShapeHandler {
  public constructor(
    private fundamentalWidth: number,
    private fundamentalHeight: number,
    private margin: number,
  ) {
    super();
  }

  public clipsPainting() {
    return false;
  }

  public paint(_context: CanvasRenderingContext2D, _delta: TransformDelta) {
  }

  public paintSelectionFrame(_context: CanvasRenderingContext2D, _delta: TransformDelta) {
  }

  public getTransformedRect(_delta: TransformDelta = emptyTransformDelta): Rect {
    return this.getEnclosingRect();
  }

  public getEnclosingRect(): Rect {
    return {
      x: -this.margin, y: -this.margin,
      width: this.fundamentalWidth + 2 * this.margin,
      height: this.fundamentalHeight + 2 * this.margin,
    };
  }

  public getSelectionMarkPositions(_delta: TransformDelta = emptyTransformDelta): NamedPoint[] {
    return [];
  }

  public isPointInLayer(_pos: Point) {
    return true;
  }
}

abstract class DrawingShapeHandler extends ShapeHandler {
  public constructor(public shape: DrawingShape) {
    super();
  }

  public getEnclosingRect(): Rect {
    return getInflatedRect(this.shape, this.shape.lineWidth / 2);
  }

  public isPointInShape(pos: Point, context: CanvasRenderingContext2D) {
    this.setShapePath(context);
    return context.isPointInPath(pos.x, pos.y);
  }
}

abstract class FigureShapeHandler extends DrawingShapeHandler {
  public constructor(public shape: FigureShape) {
    super(shape);
  }

  public getSelectionMarkPositions(delta: TransformDelta = emptyTransformDelta): NamedPoint[] {
    const rect = this.getTransformedRect(delta);
    switch (this.shape.kind) {
      case ShapeKind.Square:
      case ShapeKind.Circle:
      case ShapeKind.EquilateralTriangle:
        return getCornerPointsFromRect(rect);
      default:
        return getCornerPointsFromRect(rect).concat(getMiddlePointsFromRect(rect));
    }
  }
}

class RectShapeHandler extends FigureShapeHandler {
  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    const { x, y, width, height } = this.shape;
    const rect = transformRect({ x, y, width, height }, delta);
    const { fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled } = this.shape;
    drawRect(
      context, rect,
      fillColor, lineColor,
      lineWidth, lineType,
      lineCaps, alpha, isFilled, false,
    );
  }
}

class LearningPenRectShapeHandler extends FigureShapeHandler {
  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    const { x, y, width, height } = this.shape;
    const rect = transformRect({ x, y, width, height }, delta);
    const { fillColor, lineColor, lineType, lineCaps, alpha } = this.shape;
    drawRect(
      context, rect,
      fillColor, lineColor,
      0, lineType,
      lineCaps, alpha, true, false,
    );
  }

  public getEnclosingRect(): Rect {
    return getInflatedRect(this.shape, 0);
  }
}

class CircleShapeHandler extends FigureShapeHandler {
  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    const { x, y, width, height } = this.shape;
    const rect = transformRect({ x, y, width, height }, delta);
    const { fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled } = this.shape;
    drawOval(context, rect, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled);
  }

  public setShapePath(
    context: CanvasRenderingContext2D, delta: TransformDelta = emptyTransformDelta,
  ) {
    const rect = this.getTransformedRect(delta);
    setOvalPath(context, rect);
  }
}

class TriangleShapeHandler extends FigureShapeHandler {
  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    const { x, y, width, height } = this.shape;
    const rect = transformRect({ x, y, width, height }, delta);
    const { fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled } = this.shape;
    drawTriangle(
      context, rect, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
    );
  }

  public setShapePath(
    context: CanvasRenderingContext2D, delta: TransformDelta = emptyTransformDelta,
  ) {
    const rect = this.getTransformedRect(delta);
    setTrianglePath(context, rect);
  }
}

abstract class NoteShapeHandler extends DrawingShapeHandler {
  public static widthFolded = 100 * fundamentalSizeMultiplier; // 折りたたまれ状態の仮UIに使う幅
  public static heightFolded = 100 * fundamentalSizeMultiplier; // 折りたたまれ状態の仮UIに使う高さ
  public abstract getFoldedRect(): Rect;
  public abstract paintArranged(
    context: CanvasRenderingContext2D, delta: TransformDelta, shape: NoteShape,
  ): void;

  public constructor(public shape: NoteShape) {
    super(shape);
  }

  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    this.paintArranged(context, delta, this.shape);
  }

  public paintVisible(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    const isTransparent = this.shape.alpha === 1 ? false : true;
    const attr = { ...this.shape, isTransparent };
    const attrVisible = { ...this.shape, ...getVisibleDrawingAttr(attr) };
    this.paintArranged(context, delta, attrVisible);
  }

  public paintFolded(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    // TODO: 折りたたまれ状態のUIを図形で描くかアイコン画像にするか決める
    // 透明のノートを折りたたむと完全にどこにあるかわからないので適当に点線で囲っている
    const rect = this.getFoldedRect();
    const lineColor = this.isVisible() ? this.shape.lineColor : 'black';
    const lineType = this.isVisible() ? this.shape.lineType : LineType.Dotted;
    const alpha = this.isVisible() ? this.shape.alpha : 0.5;
    this.paintArranged(context, delta, { ...this.shape, ...rect, lineColor, lineType, alpha });
  }

  public paintText(context: CanvasRenderingContext2D, delta: TransformDelta) {
    const { x, y, width, height, text, textColor, fontSize, lineHeight, padding } = this.shape;
    const rect = isTransformResizeOrMove(delta)
      ? transformRect({ x, y, width, height }, delta)
      : { x, y, width, height };
    const textRect = getInflatedRect(
      rect,
      -(padding * textBoxDefaultPaddingSize * fundamentalSizeMultiplier),
    );

    context.save();
    this.clipShapeRegion(context, delta);
    context.font = this.getFont();
    context.fillStyle = textColor;
    context.textBaseline = 'bottom';
    fillMultilineText(
      context,
      text,
      textRect.x,
      textRect.y + fontSize + ((lineHeight - 1) * fontSize / 2),
      textRect.width,
      lineHeight * fontSize,
    );
    context.restore();
  }

  // TODO: 仮置きでない適切なフォントを考えること
  public getFont() {
    const gothic = '"Yu Gothic", YuGothic, "ヒラギノ角ゴ ProN W3", Hiragino Kaku Gothic ProN, ';
    const mincho = '"游明朝" , "Yu Mincho" , "游明朝体" , "YuMincho" , "ヒラギノ明朝 Pro W3" ,' +
      '"Hiragino Mincho Pro" , "HiraMinProN-W3" , "HGS明朝E" , "ＭＳ Ｐ明朝" , "MS PMincho", ';
    const { fontSize, fontType } = this.shape;

    return `${fontSize}px `
      + `"Helvetica Neue", ${fontType === FontType.Mincho ? mincho : gothic} `
      + '"メイリオ", Meiryo, sans-serif';
  }

  private isVisible(): boolean {
    return !(this.shape.fillColor === 'transparent' && this.shape.lineColor === 'transparent');
  }

  public isFolded(): boolean {
    return this.shape.isFolded;
  }
}

class StickyShapeHandler extends NoteShapeHandler {
  public constructor(public shape: StickyShape) {
    super(shape);
  }

  public paintArranged(
    context: CanvasRenderingContext2D, delta: TransformDelta, shape: StickyShape,
  ): void {
    const {
      x, y,
      width, height,
      fillColor, lineColor,
      lineWidth, lineType,
      lineCaps, alpha, isFilled,
    } = shape;
    const rect = transformRect({ x, y, width, height }, delta);
    drawRect(
      context, rect,
      fillColor, lineColor,
      lineWidth, lineType,
      lineCaps, alpha, isFilled, true,
    );
  }

  public getFoldedRect(): Rect {
    const { x, y } = this.shape;
    return { x, y, width: NoteShapeHandler.widthFolded, height: NoteShapeHandler.heightFolded };
  }
}

class BalloonShapeHandler extends NoteShapeHandler {
  public constructor(public shape: BalloonShape) {
    super(shape);
  }

  public paintArranged(
    context: CanvasRenderingContext2D, delta: TransformDelta, shape: BalloonShape,
  ): void {
    const {
      x, y, width, height, fillColor, lineColor, lineWidth, lineType, lineCaps,
      alpha, isFilled, arrowPoint,
    } = shape;

    const rect = isTransformResizeOrMove(delta)
      ? transformRect({ x, y, width, height }, delta)
      : { x, y, width, height };

    const movedArrowPoint = doseTransformMoveBalloonArrow(delta)
      ? { x: arrowPoint.x + delta.x, y: arrowPoint.y + delta.y }
      : arrowPoint;

    drawBalloon(
      context, rect, fillColor, lineColor, lineWidth, lineType, lineCaps,
      alpha, isFilled, movedArrowPoint,
    );
  }

  public getSelectionMarkPositions(delta: TransformDelta = emptyTransformDelta): NamedPoint[] {
    const rect = this.getTransformedRect(delta);
    const arrowPoint = { ...this.shape.arrowPoint, name: PointName.ArrowTip };
    const movedArrowPoint = doseTransformMoveBalloonArrow(delta)
      ? { ...arrowPoint, x: arrowPoint.x + delta.x, y: arrowPoint.y + delta.y }
      : arrowPoint;

    return getCornerPointsFromRect(rect)
      .concat(getMiddlePointsFromRect(rect))
      .concat([movedArrowPoint]);
  }

  public getFoldedRect(): Rect {
    const { arrowPoint } = this.shape;
    // 以下のようなRectを返す(仮UIであまり考えていないのでバグっている可能性あり)
    // - 矢印の長さは20くらい
    // - 矢印と四角の位置関係は保つ
    // FIXME: 折りたたまれUIより小さい吹き出しではバグるはず そろそろ吹き出しのサイズの下限を決める必要がありそう
    const balloonCenter = getCenterPointFromRect(this.shape);
    const lineFromCenterToArrow = { begin: balloonCenter, end: arrowPoint };
    const arrowLen = 20;
    const rectWidthHalf = NoteShapeHandler.widthFolded / 2;
    const rectForIntersectionPoint = getRectFromCenterPoint(
      arrowPoint,
      arrowLen + rectWidthHalf,
      arrowLen + rectWidthHalf,
    );
    const intersectionPoints = getIntersectionPointOfRectAndLine(
      rectForIntersectionPoint, lineFromCenterToArrow,
    );
    const closestPoint = findClosestPoint(balloonCenter, intersectionPoints);
    if (closestPoint !== null) {
      return getRectFromCenterPoint(closestPoint, rectWidthHalf, rectWidthHalf);
    }

    // 見つからなかったら適当に左下矢印になる吹き出しを返す
    return getRectFromCenterPoint(
      { x: arrowPoint.x + arrowLen, y: arrowPoint.y - NoteShapeHandler.heightFolded + arrowLen },
      rectWidthHalf,
      NoteShapeHandler.heightFolded,
    );
  }
}

class StraightLineShapeHandler extends ShapeHandler {
  public constructor(public shape: StraightLineShape) {
    super();
  }

  public isLayerDrawable() {
    return false;
  }

  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    const { lineColor, lineWidth, lineType, lineCaps, alpha } = this.shape;
    const transformedLine = isTransformResizeOrMove(delta)
      ? moveStraightLineShape(this.shape, delta.x, delta.y).line
      : moveStraightLineShapeVertex(this.shape, delta).line;
    drawLine(context, transformedLine, lineColor, lineWidth, lineType, lineCaps, alpha);
  }

  public paintSelectionFrame(
    _context: CanvasRenderingContext2D, _delta: TransformDelta, _scale: number,
  ) {
  }

  public getSelectionMarkPositions(delta: TransformDelta = emptyTransformDelta): NamedPoint[] {
    const transformedLine = isTransformResizeOrMove(delta)
      ? moveStraightLineShape(this.shape, delta.x, delta.y).line
      : moveStraightLineShapeVertex(this.shape, delta).line;
    return [
      { ...transformedLine.begin, name: PointName.LineBegin },
      { ...transformedLine.end, name: PointName.LineEnd },
    ];
  }

  public isPointInShape(pos: Point, context: CanvasRenderingContext2D) {
    const { begin, end } = this.shape.line;
    setLinePathAsRect(context, [begin, end], getClickableLineWidth(this.shape.lineWidth));
    return context.isPointInPath(pos.x, pos.y);
  }

  public setShapePath(
    context: CanvasRenderingContext2D, _delta: TransformDelta = emptyTransformDelta,
  ) {
    const { begin, end } = this.shape.line;
    context.beginPath();
    context.moveTo(begin.x, begin.y);
    context.lineTo(end.x, end.y);
  }

  public getEnclosingRect(): Rect {
    return getRectFromPoints(this.shape.line.begin, this.shape.line.end);
  }
}

abstract class MultiVertexShapeHandler extends ShapeHandler {
  public abstract draw(context: CanvasRenderingContext2D, vertices: ReadonlyArray<Point>): void;

  public constructor(public shape: MultiVertexShape) {
    super();
  }

  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    this.draw(context, this.getTransformedVertices(delta));
  }

  public getSelectionMarkPositions(delta: TransformDelta = emptyTransformDelta): NamedPoint[] {
    const rect = this.getTransformedRect(delta);
    const vertices = this.getTransformedVertices(delta).map((vertex: Point) => {
      return { ...vertex, name: PointName.PolygonVertex };
    });

    return getCornerPointsFromRect(rect).concat(getMiddlePointsFromRect(rect)).concat(vertices);
  }

  public getEnclosingRect(): Rect {
    return getRectCoveringAllPoints(this.shape.vertices);
  }

  protected getTransformedVertices(
    delta: TransformDelta = emptyTransformDelta,
  ): ReadonlyArray<Point> {
    return isTransformResizeOrMove(delta)
      ? transformVertices(this.shape.vertices, delta)
      : moveMatchingVertex(this.shape.vertices, delta);
  }
}

class PolylineShapeHandler extends MultiVertexShapeHandler {
  public constructor(public shape: PolylineShape) {
    super(shape);
  }

  public draw(context: CanvasRenderingContext2D, vertices: ReadonlyArray<Point>) {
    const { lineColor, lineWidth, lineType, lineCaps, alpha } = this.shape;
    drawPolyline(context, vertices, lineColor, lineWidth, lineType, lineCaps, alpha);
  }
}

class PolygonShapeHandler extends MultiVertexShapeHandler {
  public constructor(public shape: PolygonShape) {
    super(shape);
  }

  public draw(context: CanvasRenderingContext2D, vertices: ReadonlyArray<Point>) {
    const { fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled } = this.shape;
    drawPolygon(
      context, vertices, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
    );
  }

  public isPointInShape(pos: Point, context: CanvasRenderingContext2D) {
    this.setShapePath(context);
    return context.isPointInPath(pos.x, pos.y);
  }

  public setShapePath(
    context: CanvasRenderingContext2D, delta: TransformDelta = emptyTransformDelta,
  ) {
    setPolygonPath(context, this.getTransformedVertices(delta));
  }
}

class ImageFileShapeHandler extends ShapeHandler {
  public constructor(private shape: ImageFileShape, private texture: HTMLCanvasElement) {
    super();
  }

  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    const rect = this.getTransformedRect(delta);
    context.save();
    context.globalAlpha = this.shape.alpha;
    context.drawImage(this.texture, rect.x, rect.y, rect.width, rect.height);
    context.restore();
  }

  public getEnclosingRect(): Rect {
    return this.shape;
  }
}

class StampShapeHandler extends ShapeHandler {
  public constructor(private shape: StampShape, private texture: HTMLCanvasElement) {
    super();
  }

  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    const rect = this.getTransformedRect(delta);
    context.save();
    context.drawImage(this.texture, rect.x, rect.y, rect.width, rect.height);
    context.restore();
  }

  public getEnclosingRect(): Rect {
    return this.shape;
  }
}

class MultiroleLinkShapeHandler extends ShapeHandler {
  public constructor(public shape: MultiroleLinkShape, private iconImage: HTMLImageElement) {
    super();
  }

  public paint(context: CanvasRenderingContext2D, delta: TransformDelta): void {
    // TODO: アイコン差し替え 種類に応じたアイコン 色は本当に必要かどうか
    const { multiroleLink } = this.shape;
    const rect = transformRect(
      {
        x: this.shape.x,
        y: this.shape.y,
        width: this.iconImage.width,
        height: this.iconImage.height,
      },
      delta,
    );
    const { x, y, width, height } = rect;

    context.save();

    context.fillStyle = multiroleLink.color;
    context.fillRect(x, y, width, height);
    context.drawImage(this.iconImage, x, y);

    const fontSize = width / 5;
    context.font = `${fontSize}px arial`;
    context.fillStyle = 'white';
    context.textBaseline = 'ideographic';
    let text = '';
    switch (multiroleLink.kind) {
      case MultiroleLinkKind.File:
        text = 'file';
        break;
      case MultiroleLinkKind.Web:
        text = 'web';
        break;
      case MultiroleLinkKind.Page:
        text = `${multiroleLink.page}`;
        break;
    }
    context.fillText(text, x, y + fontSize, width);
    context.restore();
  }

  public getSelectionMarkPositions(_delta: TransformDelta = emptyTransformDelta): NamedPoint[] {
    return [];
  }

  public isLayerDrawable() {
    return false;
  }

  public getEnclosingRect(): Rect {
    const { x, y } = this.shape;
    const { width, height } = this.iconImage;
    return { x, y, width, height };
  }
}

export default class DrawingCanvas extends React.Component<
  DrawingCanvasPropsType, DrawingCanvasState
  > {
  private hitTestingCanvas: HTMLCanvasElement;
  private intermediateTiles: HTMLCanvasElement[] = [];
  private visibleCanvas: HTMLCanvasElement | null;
  private multiroleLinkIcon: HTMLImageElement;
  private capturing: boolean = false;

  public constructor(props: DrawingCanvasPropsType) {
    super(props);

    this.state = {
      work: { mode: DrawingMode.Disabled },
      editingText: false,
    };

    this.hitTestingCanvas = document.createElement('canvas');
    this.hitTestingCanvas.width = 1;
    this.hitTestingCanvas.height = 1;
    this.replenishIntermediateTilesIfNeeded(props);

    this.visibleCanvas = null;

    this.multiroleLinkIcon = document.createElement('img');
    this.multiroleLinkIcon.onload = () => this.repaint();
    this.multiroleLinkIcon.src = './images/tool-icons/mac-dock-url-icon.png';
  }

  private intermediateTile(row: number, column: number) {
    const { margin } = this.props;
    const minColumn = Math.floor(-margin / imageTileSize) - 1;
    const maxColumn = Math.ceil((this.props.fundamentalWidth + margin) / imageTileSize) + 1;
    const minRow = Math.floor(-margin / imageTileSize) - 1;
    const maxRow = Math.ceil((this.props.fundamentalHeight + margin) / imageTileSize) + 1;

    if (row < minRow || maxRow <= row || column < minColumn || maxColumn <= column) {
      return null;
    }

    return this.intermediateTiles[(maxColumn - minColumn) * (row - minRow) + (column - minColumn)];
  }

  private handlePastingImageLoad(source: Blob, img: HTMLImageElement) {
    const { width, height } = img;
    const pastingImages = this.props.toolState.pastingImages.slice();

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (context === null) {
      throw new Error('Failed to get context.');
    }

    context.drawImage(img, 0, 0);

    for (let index = 0; index < pastingImages.length; index += 1) {
      const pastingImage = pastingImages[index];
      if (pastingImage.source === source) {
        const isSelected = (source === this.props.toolState.imageFileToPaste);
        switch (pastingImage.state) {
          case PastingImageState.Loading:
            if (isSelected) {
              break;
            }

            throw new Error('Illegal state.');

          case PastingImageState.LoadingWithCallback:
            break;

          default:
            throw new Error('Illegal state.');
        }

        if (pastingImage.state === PastingImageState.LoadingWithCallback) {
          const entry = { picture: canvas, id: generateAttachmentID() };
          pastingImage.callback(entry);
          pastingImages[index] = {
            source, entry,
            state: PastingImageState.Pasted,
          };
        } else {
          pastingImages[index] = {
            source,
            texture: canvas,
            state: PastingImageState.Ready,
          };
        }

        if (!isSelected) {
          pastingImages.splice(index, 1);
        }

        break;
      }
    }

    this.props.onSetPastingImages(pastingImages);
  }

  private handlePastingImageError(source: Blob) {
    const pastingImages = this.props.toolState.pastingImages.slice();
    const isSelected = (source === this.props.toolState.imageFileToPaste);

    if (isSelected) {
      alert('画像ファイルとして読み込めませんでした。');

      // TODO : Clear the file field.
    }

    for (let index = 0; index < pastingImages.length; index += 1) {
      const pastingImage = pastingImages[index];
      if (pastingImage.source === source) {
        switch (pastingImage.state) {
          case PastingImageState.Loading:
            if (isSelected) {
              break;
            }

            throw new Error('Illegal state.');

          case PastingImageState.LoadingWithCallback:
            break;

          default:
            throw new Error('Illegal state.');
        }

        if (isSelected) {
          pastingImages[index] = {
            source,
            state: PastingImageState.Error,
          };
        } else {
          pastingImages.splice(index, 1);
        }

        break;
      }
    }

    this.props.onSetPastingImages(pastingImages);
  }

  private getPastingImages(
    props: Readonly<DrawingCanvasProps>,
    prevImages: ReadonlyArray<PastingImage>,
  ) {
    const { imageFileToPaste } = props.toolState;

    let removeLast = false;

    if (prevImages.length > 0) {
      const lastImage = prevImages[prevImages.length - 1];

      if (lastImage.source === imageFileToPaste) {
        return prevImages;
      }

      switch (lastImage.state) {
        case PastingImageState.Loading:
          lastImage.loader.disconnect();
          removeLast = true;
          break;

        case PastingImageState.LoadingWithCallback:
          break;

        default:
          removeLast = true;
          break;
      }
    }

    if (imageFileToPaste === null && !removeLast) {
      return prevImages;
    }

    const images = prevImages.slice();

    if (removeLast) {
      images.pop();
    }

    if (imageFileToPaste === null) {
      return images;
    }

    const loader = new BlobImageLoader(
      imageFileToPaste,
      (img) => {
        this.handlePastingImageLoad(imageFileToPaste, img);
      },
      () => {
        this.handlePastingImageError(imageFileToPaste);
      },
    );

    images.push({
      loader,
      state: PastingImageState.Loading,
      source: imageFileToPaste,
    });
    return images;
  }

  private getStampImageState(stampId: string): StampImageState {
    const { stampImageCaches } = this.props.toolState;
    const state = stampImageCaches[stampId];

    if (state) {
      return state;
    }

    const stamp: Stamp = this.props.stamps.filter(stamp => stamp.id === stampId)[0];

    if (!stamp) {
      throw new Error('Stamp is not found.');
    }

    const image = new Image();
    const canvas = document.createElement('canvas');

    const entry: ImageEntry = {
      id: generateAttachmentID(),
      picture: canvas,
    };
    const imageState: StampImageState = {
      entry,
      loaded: false,
    };
    image.src = stamp.path;

    const promise = new Promise<StampImageState>((resolve) => {
      image.onload = () => {
        canvas.width = image.width;
        canvas.height = image.height;

        const context = canvas.getContext('2d');
        if (context === null) {
          throw new Error('Failed to get context.');
        }

        context.drawImage(image, 0, 0);
        imageState.loaded = true;
        resolve(imageState);
      };
    });

    imageState.loadPromise = promise;
    stampImageCaches[stamp.id] = imageState;
    return imageState;
  }

  private replenishIntermediateTilesIfNeeded(
    props: Readonly<DrawingCanvasProps>,
    prevProps: Readonly<DrawingCanvasProps> | null = null,
  ) {
    let changed = false;
    if (prevProps === null) {
      changed = true;
    } else {
      const newWidth = props.fundamentalWidth + 2 * props.margin;
      const newHeight = props.fundamentalHeight + 2 * props.margin;
      const oldWidth = prevProps.fundamentalWidth + 2 * prevProps.margin;
      const oldHeight = prevProps.fundamentalHeight + 2 * prevProps.margin;
      if (oldWidth !== newWidth || oldHeight !== newHeight) {
        changed = true;
      }
    }

    if (changed) {
      const { margin } = props;
      // Extra tiles are for movement of layer.
      const minColumn = Math.floor(-margin / imageTileSize) - 1;
      const maxColumn = Math.ceil((props.fundamentalWidth + margin) / imageTileSize) + 1;
      const minRow = Math.floor(-margin / imageTileSize) - 1;
      const maxRow = Math.ceil((props.fundamentalHeight + margin) / imageTileSize) + 1;
      const numTiles = (maxRow - minRow) * (maxColumn - minColumn);

      while (this.intermediateTiles.length < numTiles) {
        const tile = document.createElement('canvas');
        tile.width = imageTileSize;
        tile.height = imageTileSize;
        this.intermediateTiles.push(tile);
      }
    }
  }

  private isLayerVisible(layerID: number): boolean {
    const { props } = this;
    const { visibleShapeKinds } = props.toolState;
    const { shape } = props.data.layers[layerID];
    return shape.kind === ShapeKind.None || visibleShapeKinds.indexOf(shape.kind) !== -1;
  }

  private getShapePaintMode(layerID: number): PaintMode {
    if (this.props.toolState.mode === DrawingMode.Disabled) {
      const shapeHandler = this.getNoteShapeHandler(layerID);
      if (shapeHandler !== null && shapeHandler.isFolded()) {
        // 仕様が未確定ではあるが、書き込み時に折りたたまれていると不便なはずなので、
        // 書き込みモード以外でのみ折りたたみ状態で描く
        return PaintMode.Folded;
      }
      return PaintMode.Normal;
    }
    // 仕様が未確定ではあるが、書き込み時に透明だと不便なはずなので、
    // 書き込みモードでは透明のShapeでもできるだけ視認できるように描く
    return PaintMode.Visible;
  }

  private isHandwritingVisible(): boolean {
    return this.props.toolState.showHandwriting;
  }

  private getImage(pictureID: string) {
    const { pictureStorage } = this.props;
    if (!(pictureID in pictureStorage.items)) {
      throw new Error('Illegal state.');
    }

    const { image } = pictureStorage.items[pictureID];
    if (image === null) {
      throw new Error('Illegal state.');
    }

    return image;
  }

  private getShapeHandler(layerID: number): ShapeHandler {
    const { props } = this;
    const { shape } = props.data.layers[layerID];
    switch (shape.kind) {
      case ShapeKind.None:
        return new NullShapeHandler(props.fundamentalWidth, props.fundamentalHeight, props.margin);

      case ShapeKind.Rect:
      case ShapeKind.Square:
        return new RectShapeHandler(shape);

      case ShapeKind.Oval:
      case ShapeKind.Circle:
        return new CircleShapeHandler(shape);

      case ShapeKind.Triangle:
      case ShapeKind.EquilateralTriangle:
        return new TriangleShapeHandler(shape);

      case ShapeKind.Sticky:
        return new StickyShapeHandler(shape);

      case ShapeKind.Balloon:
        return new BalloonShapeHandler(shape);

      case ShapeKind.StraightLine:
      case ShapeKind.AxisAlignedStraightLine:
        return new StraightLineShapeHandler(shape);

      case ShapeKind.LearningPenRect:
        return new LearningPenRectShapeHandler(shape);

      case ShapeKind.Polyline:
        return new PolylineShapeHandler(shape);

      case ShapeKind.Polygon:
        return new PolygonShapeHandler(shape);

      case ShapeKind.ImageFile:
        return new ImageFileShapeHandler(
          shape,
          this.getImage(shape.pictureID),
        );

      case ShapeKind.Stamp:
        const stampState = this.getStampImageState(shape.stampId);

        if (!stampState.loaded && stampState.loadPromise) {
          stampState.loadPromise.then(() => { this.repaint(); });
        }

        const picture = stampState.entry.picture;

        return new StampShapeHandler(
          shape,
          picture,
        );
      case ShapeKind.MultiroleLink:
        return new MultiroleLinkShapeHandler(shape, this.multiroleLinkIcon);
    }
  }

  private getNoteShapeHandler(layerID: number): NoteShapeHandler | null {
    const { props } = this;
    const { shape } = props.data.layers[layerID];
    switch (shape.kind) {
      case ShapeKind.Sticky:
        return new StickyShapeHandler(shape);

      case ShapeKind.Balloon:
        return new BalloonShapeHandler(shape);

      default:
        return null;
    }
  }

  private resetActiveLayer() {
    const { layerOrder } = this.props.data;

    for (let ordinal = layerOrder.length - 1; ordinal >= 0; ordinal -= 1) {
      if (this.isLayerVisible(layerOrder[ordinal])) {
        this.props.onDrawingAction(activateLayer(layerOrder[ordinal]));
        break;
      }
    }
  }

  private activateLayerAt(pos: Point): number {
    const { props } = this;

    const context = this.hitTestingCanvas.getContext('2d');
    if (context === null) {
      throw new Error('Failed to get context.');
    }

    const { layerOrder } = props.data;
    for (let ordinal = layerOrder.length; ordinal > 0; ordinal -= 1) {
      const layerID = layerOrder[ordinal - 1];
      if (this.isLayerVisible(layerID)
        && this.getShapeHandler(layerID).isPointInShape(pos, context)) {
        props.onDrawingAction(activateLayer(layerID));
        return layerID;
      }
    }
    return -1;
  }

  private transformPoint(orig: Point): Point {
    const { props } = this;
    return scalePoint(
      { x: orig.x - props.translateX, y: orig.y - props.translateY },
      1 / props.scale,
    );
  }

  private getDrawingPoint(ev: UIEvent<HTMLCanvasElement>): Point {
    return this.transformPoint(getClientPoint(ev));
  }

  private isPointInSelectionMark(pos: Point, x: number, y: number): boolean {
    // For ease of click, clickable area has been larger than selection mark.
    const half = 30 / this.props.scale;
    return isPointInRect(pos, getRectFromCenterPoint({ x, y }, half, half));
  }

  private getSelectionMarkPositionAtPoint(pos: Point): NamedPoint {
    const layerID = this.props.data.activeLayer;
    const positions = this.getShapeHandler(layerID).getSelectionMarkPositions();

    let selectionMark: NamedPoint = unnamedPoint;
    for (const position of positions) {
      if (this.isPointInSelectionMark(pos, position.x, position.y)) {
        selectionMark = position;
      }
    }
    return selectionMark;
  }

  private getFreeResizingDelta(layerID: number): Rect {
    let delta = { x: 0, y: 0, width: 0, height: 0 };
    if (this.props.data.activeLayer === layerID) {
      const { work } = this.state;
      if (work.mode === DrawingMode.Resizing) {
        const offsetX = work.offset.x;
        const offsetY = work.offset.y;
        switch (work.offset.origin.name) {
          case PointName.TopLeft:
            delta = { x: offsetX, y: offsetY, width: -offsetX, height: -offsetY };
            break;
          case PointName.TopCenter:
            delta = { x: 0, y: offsetY, width: 0, height: -offsetY };
            break;
          case PointName.TopRight:
            delta = { x: 0, y: offsetY, width: offsetX, height: -offsetY };
            break;
          case PointName.MiddleLeft:
            delta = { x: offsetX, y: 0, width: -offsetX, height: 0 };
            break;
          case PointName.MiddleRight:
            delta = { x: 0, y: 0, width: offsetX, height: 0 };
            break;
          case PointName.BottomLeft:
            delta = { x: offsetX, y: 0, width: -offsetX, height: offsetY };
            break;
          case PointName.BottomCenter:
            delta = { x: 0, y: 0, width: 0, height: offsetY };
            break;
          case PointName.BottomRight:
            delta = { x: 0, y: 0, width: offsetX, height: offsetY };
            break;
          default:
            delta = { x: 0, y: 0, width: 0, height: 0 };
            break;
        }
      }
    }
    return delta;
  }

  private getUniformResizingDelta(layerID: number): Rect {
    let delta = { x: 0, y: 0, width: 0, height: 0 };
    if (this.props.data.activeLayer === layerID) {
      const { work } = this.state;
      if (work.mode === DrawingMode.Resizing) {
        const offsetX = work.offset.x;
        switch (work.offset.origin.name) {
          case PointName.TopLeft:
            delta = { x: offsetX, y: offsetX, width: -offsetX, height: -offsetX };
            break;
          case PointName.TopRight:
            delta = { x: 0, y: -offsetX, width: offsetX, height: offsetX };
            break;
          case PointName.BottomLeft:
            delta = { x: offsetX, y: 0, width: -offsetX, height: -offsetX };
            break;
          case PointName.BottomRight:
            delta = { x: 0, y: 0, width: offsetX, height: offsetX };
            break;
          default:
            break;
        }

        const { shape } = this.props.data.layers[layerID];
        if (shape.kind === ShapeKind.EquilateralTriangle) {
          const { x, y, width, height } = delta;
          const aspectRatio = shape.height / shape.width;
          delta = { x, width, y: y * aspectRatio, height: height * aspectRatio };
        }
      }
    }

    return delta;
  }

  private getShapeTransformDelta(layerID: number): TransformDelta {
    if (this.props.data.activeLayer === layerID) {
      const { work } = this.state;
      switch (work.mode) {
        case DrawingMode.Selection:
        case DrawingMode.BalloonArrowMoving:
        case DrawingMode.PointMoving: {
          return { ...work.offset, width: 0, height: 0 };
        }

        case DrawingMode.Resizing: {
          const { shape } = this.props.data.layers[layerID];
          switch (shape.kind) {
            case ShapeKind.Square:
            case ShapeKind.Circle:
            case ShapeKind.EquilateralTriangle:
              return { ...this.getUniformResizingDelta(layerID), origin: work.offset.origin };
            default:
              return { ...this.getFreeResizingDelta(layerID), origin: work.offset.origin };
          }
        }
        default:
          break;
      }
    }
    return emptyTransformDelta;
  }

  private currentDrawingAttr(): DrawingAttrAndAlpha {
    const { mode, drawingAttrs, alpha } = this.props.toolState;
    return drawingAttrs[mode].isTransparent
      ? { ...drawingAttrs[mode], alpha }
      : { ...drawingAttrs[mode], alpha: 1 };
  }

  private createAddFigureAction(kind: ShapeKindGroupFigure, rect: Rect): AddFigureAction | null {
    const {
      fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
    } = this.currentDrawingAttr();
    const { x, y, width, height } = rect;
    if (width > 0 && height > 0) {
      return addFigure(
        kind, x, y, width, height, fillColor, lineColor, lineWidth,
        lineType, lineCaps, alpha, isFilled,
      );
    }
    return null;
  }

  private clipShapeRegion(
    context: CanvasRenderingContext2D,
    layerID: number,
    delta: TransformDelta,
    isPainting: boolean = true,
  ) {
    const shapeHandler = this.getShapeHandler(layerID);
    if (!isPainting || shapeHandler.clipsPainting()) {
      shapeHandler.clipShapeRegion(context, delta);
    }
  }

  private setCapture() {
    if (this.visibleCanvas === null) {
      throw new Error('Illegal state.');
    }

    this.capturing = true;

    const { ownerDocument } = this.visibleCanvas;
    if (ownerDocument === null) {
      return;
    }

    if (hasPointerEvents()) {
      ownerDocument.addEventListener('pointermove', this.handlePointerMoveDragging);
      ownerDocument.addEventListener('pointerup', this.handlePointerUp);
      return;
    }

    ownerDocument.addEventListener('mousemove', this.handleMouseMoveDragging);
    ownerDocument.addEventListener('mouseup', this.handlePointerUp);
    ownerDocument.addEventListener('touchmove', this.handleTouchMove);
    ownerDocument.addEventListener('touchend', this.handlePointerUp);
  }

  private releaseCapture() {
    if (this.visibleCanvas === null) {
      return;
    }

    this.capturing = false;

    const { ownerDocument } = this.visibleCanvas;
    if (ownerDocument === null) {
      return;
    }

    if (hasPointerEvents()) {
      ownerDocument.removeEventListener('pointermove', this.handlePointerMoveDragging);
      ownerDocument.removeEventListener('pointerup', this.handlePointerUp);
      return;
    }

    ownerDocument.removeEventListener('mousemove', this.handleMouseMoveDragging);
    ownerDocument.removeEventListener('mouseup', this.handlePointerUp);
    ownerDocument.removeEventListener('touchmove', this.handleTouchMove);
    ownerDocument.removeEventListener('touchend', this.handlePointerUp);
  }

  private handlePointerDown = (ev: UIEvent<HTMLCanvasElement>) => {
    ev.preventDefault();

    if (this.props.onRequestFocus) {
      this.props.onRequestFocus();
    }

    if (!this.props.focus) {
      return;
    }

    this.setState({ work: this.state.work, editingText: false });
    const { mode } = this.props.toolState;
    const drawingPoint = this.getDrawingPoint(ev);
    switch (mode) {
      case DrawingMode.Selection:
        const selectionMarkPos = this.getSelectionMarkPositionAtPoint(drawingPoint);
        const offset = { x: 0, y: 0, origin: { ...drawingPoint, name: selectionMarkPos.name } };
        switch (offset.origin.name) {
          case PointName.None: {
            const lastActiveLayerID = this.props.data.activeLayer;
            const activatedID = this.activateLayerAt(drawingPoint);
            if (activatedID === bottomLayerID) {
              return;
            }
            const activateLayerExecuted = lastActiveLayerID !== activatedID;
            this.setState({ work: { offset, mode, activateLayerExecuted } });
            break;
          }

          case PointName.ArrowTip: {
            this.setState({ work: { offset, mode: DrawingMode.BalloonArrowMoving } });
            break;
          }

          case PointName.PolygonVertex:
          case PointName.LineBegin:
          case PointName.LineEnd: {
            this.setState({
              work: {
                offset: { ...offset, origin: selectionMarkPos },
                mode: DrawingMode.PointMoving,
              },
            });
            break;
          }

          default:
            this.setState({ work: { offset, mode: DrawingMode.Resizing } });
            break;
        }
        break;
      case DrawingMode.ObjectRemoval:
        this.removeLayerAt(drawingPoint);
        this.setState({ work: { mode, hover: false } });
        break;
      case DrawingMode.Handwriting:
      case DrawingMode.Handerasing: {
        this.activateLayerAt(drawingPoint);

        const layerID = this.getShapeLayerIdAtPoint(drawingPoint);
        if (layerID === null) {
          break;
        }

        const shape = this.getShapeHandler(layerID);
        if (!shape.isLayerDrawable()) {
          break;
        }

        this.setState({
          work: {
            mode,
            vertices: [drawingPoint],
            modifiedTiles: new Tiling<HTMLCanvasElement>(),
            begin: null,
          },
        });
        break;
      }
      case DrawingMode.RectDrawing:
      case DrawingMode.SquareDrawing:
      case DrawingMode.OvalDrawing:
      case DrawingMode.CircleDrawing:
      case DrawingMode.TriangleDrawing:
      case DrawingMode.EquilateralTriangleDrawing:
      case DrawingMode.StickyDrawing:
      case DrawingMode.BalloonDrawing:
      case DrawingMode.Stamp:
      case DrawingMode.LearningPenRect: {
        this.setState({ work: { mode, start: drawingPoint, end: drawingPoint } });
        break;
      }
      case DrawingMode.StraightLineDrawing:
      case DrawingMode.AxisAlignedStraightLineDrawing: {
        this.setState({ work: { mode, begin: drawingPoint, end: drawingPoint } });
        break;
      }
      case DrawingMode.VerticalStraightLineHandWriting:
      case DrawingMode.HorizontalStraightLineHandWriting: {
        this.activateLayerAt(drawingPoint);

        const layerID = this.getShapeLayerIdAtPoint(drawingPoint);
        if (layerID === null) {
          break;
        }

        const shape = this.getShapeHandler(layerID);
        if (!shape.isLayerDrawable()) {
          break;
        }

        this.setState({ work: {
          mode,
          begin: drawingPoint,
          vertices: [drawingPoint],
          modifiedTiles: new Tiling<HTMLCanvasElement>(),
        } });
        break;
      }
      case DrawingMode.MultiVertexDrawing: {
        const {
          fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        } = this.currentDrawingAttr();
        const { work } = this.state;
        if (work.mode === DrawingMode.MultiVertexDrawing) {
          const beginPointRect = getRectFromCenterPoint(work.vertices[0], 30, 30);
          if (work.vertices.length >= 3 && isPointInRect(drawingPoint, beginPointRect)) {
            const { width, height } = getRectCoveringAllPoints(work.vertices);
            if (width === 0 || height === 0) {
              break; // 直線、または、全て同じ位置の点であるので追加しない
            }

            this.props.onDrawingAction(addPolygon(
              fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled, work.vertices,
            ));
            this.setState({ work: { mode: DrawingMode.Disabled } });
          } else {
            const lastPoint = work.vertices[work.vertices.length - 1];
            const lastPointRect = getRectFromCenterPoint(lastPoint, 30, 30);
            if (isPointInRect(drawingPoint, lastPointRect)) {
              if (work.vertices.length >= 2) {
                // 現状の作りでは塗りつぶし選択時に折れ線を作られると透明の線が引かれてしまう
                // ため、その場合は回避策として塗りつぶしの色で線を引くようにしている
                const color = lineColor === 'transparent' ? fillColor : lineColor;

                this.props.onDrawingAction(addPolyline(
                  color, lineWidth, lineType, lineCaps, alpha, work.vertices,
                ));
                this.setState({ work: { mode: DrawingMode.Disabled } });
              }
              break;
            }

            const vertices = work.vertices.concat([drawingPoint]);
            const newWork: DrawingStateMultiVertex = { mode, vertices, currentPos: drawingPoint };
            this.setState({ work: newWork });
          }
        } else {
          const work: DrawingStateMultiVertex = {
            mode, vertices: [drawingPoint], currentPos: drawingPoint,
          };
          this.setState({ work });
        }
        break;
      }
      case DrawingMode.ImagePasting:
        this.setState({ work: { mode, pos: drawingPoint } });
        break;
      case DrawingMode.MultiroleLinkPasting:
        this.setState({ work: { mode, pos: drawingPoint } });
        break;
      default:
        return;
    }

    this.setCapture();
  }

  private handlePointerUp = (ev: MouseEvent | TouchEvent) => {
    this.releaseCapture();
    ev.preventDefault();

    const { work } = this.state;
    let action: DrawingAction | null = null;
    switch (work.mode) {
      case DrawingMode.Selection:
        if (work.offset.x !== 0 || work.offset.y !== 0) {
          action = moveLayer(work.offset.x, work.offset.y);
          break;
        }

        if (work.activateLayerExecuted) {
          break;
        }

        const layerID = this.getShapeLayerIdAtPoint(work.offset.origin);
        if (layerID === null || layerID === bottomLayerID) {
          break;
        }

        if (!this.isNoteShapeLayer(layerID)) {
          break;
        }

        this.setState({ work, editingText: true });
        break;

      case DrawingMode.Resizing: {
        const layerID = this.props.data.activeLayer;
        const { x, y, width, height } = this.getShapeTransformDelta(layerID);
        action = resizeLayer(x, y, width, height);
        break;
      }

      case DrawingMode.BalloonArrowMoving:
      case DrawingMode.PointMoving: {
        const layerID = this.props.data.activeLayer;
        const { width, height, ...offset } = this.getShapeTransformDelta(layerID);
        action = movePoint(offset);
        break;
      }

      case DrawingMode.VerticalStraightLineHandWriting:
      case DrawingMode.HorizontalStraightLineHandWriting:
      case DrawingMode.Handwriting:
      case DrawingMode.Handerasing: {
        const { data } = this.props;
        let modifiedEntries = new Tiling<ImageEntry>();
        const { currentImageTiling } = data.layers[data.activeLayer];
        work.modifiedTiles.forEach((row, column, tile) => {
          if (work.mode === DrawingMode.Handerasing && !currentImageTiling.has(row, column)) {
            return;
          }

          const picture = document.createElement('canvas');
          picture.width = tile.width;
          picture.height = tile.height;

          const context = picture.getContext('2d');
          if (context === null) {
            throw new Error('Failed to get context.');
          }

          context.drawImage(tile, 0, 0);
          modifiedEntries = modifiedEntries.plus(
            row,
            column,
            { picture, id: generateAttachmentID() },
          );
        });

        this.setState({ work: { mode: DrawingMode.Disabled } });
        this.props.onDrawingAction(replaceHandwritingTiles(modifiedEntries));

        return;
      }

      case DrawingMode.RectDrawing: {
        const rect = getRectFromPoints(work.start, work.end);
        action = this.createAddFigureAction(ShapeKind.Rect, rect);
        break;
      }

      case DrawingMode.SquareDrawing: {
        const rect = getSquareRectFromPoints(work.start, work.end);
        action = this.createAddFigureAction(ShapeKind.Square, rect);
        break;
      }

      case DrawingMode.OvalDrawing: {
        const rect = getRectFromPoints(work.start, work.end);
        action = this.createAddFigureAction(ShapeKind.Oval, rect);
        break;
      }

      case DrawingMode.CircleDrawing: {
        const rect = getSquareRectFromPoints(work.start, work.end);
        action = this.createAddFigureAction(ShapeKind.Circle, rect);
        break;
      }

      case DrawingMode.TriangleDrawing: {
        const rect = getRectFromPoints(work.start, work.end);
        action = this.createAddFigureAction(ShapeKind.Triangle, rect);
        break;
      }

      case DrawingMode.EquilateralTriangleDrawing: {
        const rect = getEquilateralTriangleRectFromPoints(work.start, work.end);
        action = this.createAddFigureAction(ShapeKind.EquilateralTriangle, rect);
        break;
      }

      case DrawingMode.StickyDrawing: {
        const rect = getRectFromPoints(work.start, work.end);
        const { x, y, width, height } = rect;
        const {
          fillColor, alpha, lineColor, lineWidth,
          lineType, lineCaps, textColor, fontSize, fontType, isFilled,
          lineHeight, padding,
        } = this.currentDrawingAttr();
        if (width > 0 && height > 0) {
          action = addSticky(
            x, y, width, height, fillColor, lineColor, lineWidth,
            lineType, lineCaps, alpha, isFilled,
            '', textColor, fontSize, fontType, false,
            lineHeight, padding,
          );
          this.setState({ work, editingText: true });
        }
        break;
      }

      case DrawingMode.BalloonDrawing: {
        const rect = getRectFromPoints(work.start, work.end);
        const { x, y, width, height } = rect;
        const {
          fillColor, alpha, lineColor, lineWidth,
          lineType, lineCaps, textColor, fontSize, fontType, isFilled,
          lineHeight, padding,
        } = this.currentDrawingAttr();
        if (width > 0 && height > 0) {
          action = addBalloon(
            x, y, width, height, fillColor, lineColor, lineWidth,
            lineType, lineCaps, alpha, isFilled,
            '', textColor, fontSize, fontType, false,
            lineHeight, padding,
            getDefaultBalloonArrow(rect),
          );
          this.setState({ work, editingText: true });
        }
        break;
      }

      case DrawingMode.StraightLineDrawing: {
        const begin = work.begin.x < work.end.x ? work.begin : work.end;
        const end = begin === work.begin ? work.end : work.begin;
        if (!isPointEqual(begin, end)) {
          const { lineColor, lineWidth, lineType, lineCaps, alpha } = this.currentDrawingAttr();
          action = addStraightLine(
            ShapeKind.StraightLine, { begin, end }, lineColor, lineWidth, lineType, lineCaps, alpha,
          );
        }
        break;
      }

      case DrawingMode.LearningPenRect: {
        const { start, end } = work;
        if (!isPointEqual(start, end)) {
          const { lineWidth } = this.currentDrawingAttr();
          const rect = getLearningPenRect(start, end, lineWidth);
          action = this.createAddFigureAction(ShapeKind.LearningPenRect, rect);
        }
        break;
      }

      case DrawingMode.AxisAlignedStraightLineDrawing: {
        const { begin, end } = work;
        if (!isPointEqual(begin, end)) {
          const axisAlignedLine = getAxisAlignedLineFromPoints(begin, end);
          const { lineColor, lineWidth, lineType, lineCaps, alpha } = this.currentDrawingAttr();
          action = addStraightLine(
            ShapeKind.AxisAlignedStraightLine, axisAlignedLine, lineColor, lineWidth,
            lineType, lineCaps, alpha,
          );
        }
        break;
      }

      case DrawingMode.ImagePasting: {
        const { imageFileToPaste } = this.props.toolState;
        const { alpha } = this.currentDrawingAttr();

        if (imageFileToPaste === null) {
          break;
        }

        const pastingImages = this.getPastingImages(this.props, this.props.toolState.pastingImages);

        if (pastingImages.length === 0) {
          throw new Error('Illegal state.');
        }

        const pastingImage = pastingImages[pastingImages.length - 1];

        if (pastingImage.source !== imageFileToPaste) {
          throw new Error('Illegal state.');
        }

        switch (pastingImage.state) {
          case PastingImageState.Loading: {
            const nextPastingImages = pastingImages.slice();
            nextPastingImages[pastingImages.length - 1] = {
              ...pastingImage,
              state: PastingImageState.LoadingWithCallback,
              callback: (source) => {
                this.props.onDrawingAction(pasteImage(source, work.pos.x, work.pos.y, alpha));
              },
            };
            this.setState({ work: { mode: DrawingMode.Disabled } });
            this.props.onSetPastingImages(nextPastingImages);
            return;
          }

          case PastingImageState.LoadingWithCallback: {
            const nextPastingImages = pastingImages.slice();
            nextPastingImages[pastingImages.length - 1] = {
              ...pastingImage,
              callback: (source) => {
                pastingImage.callback(source);
                this.props.onDrawingAction(pasteImage(source, work.pos.x, work.pos.y, alpha));
              },
            };
            this.setState({ work: { mode: DrawingMode.Disabled } });
            this.props.onSetPastingImages(nextPastingImages);
            return;
          }

          case PastingImageState.Ready: {
            const { source, texture } = pastingImage;
            const entry = { picture: texture, id: generateAttachmentID() };

            this.props.onDrawingAction(pasteImage(entry, work.pos.x, work.pos.y, alpha));

            const nextPastingImages = pastingImages.slice();
            nextPastingImages[pastingImages.length - 1] = {
              source, entry,
              state: PastingImageState.Pasted,
            };
            this.setState({ work: { mode: DrawingMode.Disabled } });
            this.props.onSetPastingImages(nextPastingImages);
            return;
          }

          case PastingImageState.Pasted:
            action = pasteImage(pastingImage.entry, work.pos.x, work.pos.y, alpha);
            break;

          case PastingImageState.Error:
            break;
        }

        break;
      }

      case DrawingMode.Stamp: {
        const { stamp } = this.currentDrawingAttr();

        if (!stamp || stamp === null) {
          throw new Error('Image Path not found.');
        }
        const rect = getStampRectFromSizeAndPoints(stamp.width,
                                                   stamp.height,
                                                   work.start,
                                                   work.end);
        action = addStamp(stamp.id, rect.x, rect.y, rect.width, rect.height);
        break;
      }

      case DrawingMode.MultiroleLinkPasting: {
        const { multiroleLinkToPaste } = this.props.toolState;
        if (multiroleLinkToPaste === null) {
          break;
        }
        const multiroleLink = createMultiroleLinkInfoFromMultiroleLinkCreateInfo(
          multiroleLinkToPaste,
        );
        action = pasteMultiroleLink(work.pos.x, work.pos.y, multiroleLink);
        break;
      }

      default:
        return;
    }

    this.setState({ work: { mode: DrawingMode.Disabled } });

    if (action !== null) {
      this.props.onDrawingAction(action);
    }
  }

  private handlePointerMoveDirect = (ev: UIEvent<HTMLCanvasElement>) => {
    ev.preventDefault();

    const drawingPoint = this.getDrawingPoint(ev);
    switch (this.props.toolState.mode) {
      case DrawingMode.ObjectRemoval: {
        const hover = this.isPointInAnyShape(drawingPoint);
        this.setState({ work: { hover, mode: DrawingMode.ObjectRemoval } });
        break;
      }
      case DrawingMode.MultiVertexDrawing: {
        const { work } = this.state;
        if (work.mode === DrawingMode.MultiVertexDrawing) {
          this.setState({ work: { ...work, currentPos: drawingPoint } });
        }
        break;
      }
    }
  }

  private adjustDrawingPointsForStraightLineHandWriting(
    drawingPoints: Point[],
    work: DrawingStateWorkingData,
  ): Point[] {
    switch (work.mode) {
      case DrawingMode.VerticalStraightLineHandWriting: {
        const begin = work.begin;
        if (begin === null) {
          throw new Error('Unexpected state.');
        }

        return drawingPoints.map(point => ({ x: begin.x, y: point.y }));
      }
      case DrawingMode.HorizontalStraightLineHandWriting: {
        const begin = work.begin;
        if (begin === null) {
          throw new Error('Unexpected state.');
        }

        return drawingPoints.map(point => ({ x: point.x, y: begin.y }));
      }
      default: {
        return drawingPoints;
      }
    }
  }

  private doHandlePointerMove(drawingPoints: Point[]) {
    if (drawingPoints.length === 0) {
      throw new Error('Invalid argument.');
    }

    const { work } = this.state;
    const points = this.adjustDrawingPointsForStraightLineHandWriting(drawingPoints, work);

    const lastDrawingPoint = points[points.length - 1];
    switch (work.mode) {
      case DrawingMode.Selection:
      case DrawingMode.Resizing:
      case DrawingMode.PointMoving: {
        const dx = lastDrawingPoint.x - work.offset.origin.x;
        const dy = lastDrawingPoint.y - work.offset.origin.y;
        const offset: Offset = { ...work.offset, x: dx, y: dy };
        this.setState({ work: { ...work, offset } });
        break;
      }

      case DrawingMode.BalloonArrowMoving: {
        let closestPoint: Point | null = null;
        if (work.offset.origin.name === PointName.ArrowTip) {
          const { data } = this.props;
          const shapeHandler = this.getShapeHandler(data.activeLayer);
          const guardRect = getInflatedRect(shapeHandler.getEnclosingRect(), 50);
          if (isPointInRect(lastDrawingPoint, guardRect)) {
            const intersectionPoints = getIntersectionPointOfRectAndLine(
              guardRect, { begin: getCenterPointFromRect(guardRect), end: lastDrawingPoint },
            );
            closestPoint = findClosestPoint(lastDrawingPoint, intersectionPoints);
          }
        }
        const point = closestPoint !== null ? closestPoint : lastDrawingPoint;
        const dx = point.x - work.offset.origin.x;
        const dy = point.y - work.offset.origin.y;
        const offset: Offset = { ...work.offset, x: dx, y: dy };
        this.setState({ work: { ...work, offset } });
        break;
      }

      case DrawingMode.ObjectRemoval: {
        this.removeLayerAt(lastDrawingPoint);
        break;
      }

      case DrawingMode.VerticalStraightLineHandWriting:
      case DrawingMode.HorizontalStraightLineHandWriting:
      case DrawingMode.Handwriting:
      case DrawingMode.Handerasing: {
        const vertices = work.vertices.concat(points);
        if (vertices.length < 2) {
          throw new Error('Illegal state.');
        }

        const { data } = this.props;
        const { currentImageTiling, imageOffsetX, imageOffsetY } = data.layers[data.activeLayer];

        let workingTiles = new Tiling<true>();
        for (let index = work.vertices.length; index < vertices.length; index += 1) {
          const last = vertices[index];
          const prev = vertices[index - 1];
          getTilingFromLine(
            {
              begin: { x: prev.x - imageOffsetX, y: prev.y - imageOffsetY },
              end: { x: last.x - imageOffsetX, y: last.y - imageOffsetY },
            },
            this.currentDrawingAttr().lineWidth,
            imageTileSize,
          ).forEach((row, column) => {
            workingTiles = workingTiles.plus(row, column, true);
          });
        }

        let modifiedTiles = work.modifiedTiles;
        workingTiles.forEach((row, column) => {
          const tile = this.intermediateTile(row, column);
          if (tile === null) {
            return;
          }

          const tileOffset = {
            x: column * imageTileSize + imageOffsetX,
            y: row * imageTileSize + imageOffsetY,
          };

          const shapeHandler = this.getShapeHandler(data.activeLayer);
          const enclosingRect = shapeHandler.getEnclosingRect();
          if ((tileOffset.x + tile.width) <= enclosingRect.x
            || (enclosingRect.x + enclosingRect.width) <= tileOffset.x
            || (tileOffset.y + tile.height) <= enclosingRect.y
            || (enclosingRect.y + enclosingRect.height) <= tileOffset.y) {
            return;
          }

          const tileContext = tile.getContext('2d');
          if (tileContext === null) {
            throw new Error('Failed to get context.');
          }

          if (currentImageTiling.has(row, column)) {
            tileContext.globalCompositeOperation = 'copy';
            tileContext.drawImage(this.getImage(currentImageTiling.at(row, column)!), 0, 0);
            tileContext.globalCompositeOperation = 'source-over';
          } else {
            if (work.mode === DrawingMode.Handerasing) {
              return;
            }

            tileContext.clearRect(0, 0, tile.width, tile.height);
          }

          tileContext.save();
          tileContext.transform(1, 0, 0, 1, -tileOffset.x, -tileOffset.y);

          this.clipShapeRegion(
            tileContext,
            data.activeLayer,
            this.getShapeTransformDelta(data.activeLayer),
            false,
          );
          this.setHandstrokeStyle(tileContext, work.mode);
          drawLineStrip(tileContext, vertices);

          tileContext.restore();
          modifiedTiles = modifiedTiles.plus(row, column, tile);
        });

        this.setState({ work: { ...work, vertices, modifiedTiles } });
        break;
      }

      case DrawingMode.RectDrawing:
      case DrawingMode.SquareDrawing:
      case DrawingMode.OvalDrawing:
      case DrawingMode.CircleDrawing:
      case DrawingMode.TriangleDrawing:
      case DrawingMode.EquilateralTriangleDrawing:
      case DrawingMode.StickyDrawing:
      case DrawingMode.BalloonDrawing:
      case DrawingMode.StraightLineDrawing:
      case DrawingMode.AxisAlignedStraightLineDrawing:
      case DrawingMode.LearningPenRect:
      case DrawingMode.Stamp: {
        this.setState({ work: { ...work, end: lastDrawingPoint } });
        break;
      }

      case DrawingMode.ImagePasting: {
        this.setState({ work: { ...work, pos: lastDrawingPoint } });
        break;
      }

      default:
        break;
    }
  }

  private handlePointerMoveDragging = (ev: PointerEvent) => {
    // chrome が surface pen で pointerup を発火しなかったので、
    // 画面に接触していないことを契機に pointerup に設定しているハンドラを呼ぶようにした.
    // canary 69.0.3479.0 では pointerup が発火されるようになっているので、
    // chromeの更新を待ってもいいのだが、更新前に客先で見せることはありえるので、
    // 念のためこの処理をいれておくことにしている
    // cf. https://git.bpsinc.jp/app/ark-viewer-js/issues/106
    //
    // FIXME:
    // chrome が 69.0.3479.0 以上になったら以下の処理は削除しておくこと
    if (ev.pressure === 0) {
      this.handlePointerUp(ev);
      return;
    }

    ev.preventDefault();
    if (this.visibleCanvas !== null) {
      const clientRect = this.visibleCanvas.getBoundingClientRect();

      const points: Point[] = [];
      if (ev.getCoalescedEvents === undefined) {
        points.push(this.transformPoint({
          x: ev.pageX - clientRect.left,
          y: ev.pageY - clientRect.top,
        }));
      } else {
        const events = ev.getCoalescedEvents();
        for (const event of events) {
          points.push(this.transformPoint({
            x: event.pageX - clientRect.left,
            y: event.pageY - clientRect.top,
          }));
        }
      }

      this.doHandlePointerMove(points);
    }
  }

  private handleMouseMoveDragging = (ev: MouseEvent) => {
    ev.preventDefault();
    if (this.visibleCanvas !== null) {
      const clientRect = this.visibleCanvas.getBoundingClientRect();
      this.doHandlePointerMove([this.transformPoint({
        x: ev.pageX - clientRect.left,
        y: ev.pageY - clientRect.top,
      })]);
    }
  }

  private handleTouchMove = (ev: TouchEvent) => {
    ev.preventDefault();
    if (this.visibleCanvas !== null) {
      const clientRect = this.visibleCanvas.getBoundingClientRect();
      const touch = ev.changedTouches[0];
      this.doHandlePointerMove([this.transformPoint({
        x: touch.pageX - clientRect.left,
        y: touch.pageY - clientRect.top,
      })]);
    }
  }

  private paintShape(context: CanvasRenderingContext2D, layerID: number) {
    if (!this.isLayerVisible(layerID)) {
      return;
    }

    const shape = this.getShapeHandler(layerID);
    const delta = this.getShapeTransformDelta(layerID);
    if (this.isNoteShapeHandler(shape)) {
      const paintMode = this.getShapePaintMode(layerID);
      switch (paintMode) {
        case PaintMode.Normal:
          shape.paint(context, delta);
          break;
        case PaintMode.Visible:
          shape.paintVisible(context, delta);
          break;
        case PaintMode.Folded:
          shape.paintFolded(context, delta);
          break;
      }
      if (paintMode !== PaintMode.Folded) {
        const { activeLayer } = this.props.data;
        if (layerID !== activeLayer || !this.state.editingText) {
          shape.paintText(context, delta);
        }
      }
    } else {
      shape.paint(context, delta);
    }
  }

  private paintHandwritingPointer(context: CanvasRenderingContext2D, work: DrawingStateStroke) {
    const lastDrawingPoint = work.vertices[work.vertices.length - 1];
    const { lineWidth } = this.currentDrawingAttr();
    const pointerRect = getRectFromCenterPoint(lastDrawingPoint, lineWidth / 2, lineWidth / 2);
    const pointerRectLineWidth = 5;
    drawOval(
      context, pointerRect, 'brack', 'black', pointerRectLineWidth,
      LineType.Solid, solidRoundLineCaps, 0.5, false,
    );
  }

  private adjustTileRect(tileRect: Rectangle): Rectangle {
    const { scale, translateX, translateY } = this.props;
    const transformedLeft = scale * tileRect.x + this.props.translateX;
    const transformedTop = scale * tileRect.y + this.props.translateY;
    const transformedRight = transformedLeft + scale * tileRect.width;
    const transformedBottom = transformedTop + scale * tileRect.height;

    const roundedLeft = (Math.floor(transformedLeft) - translateX) / scale;
    const roundedTop = (Math.floor(transformedTop) - translateY) / scale;
    const roundedRight = (Math.floor(transformedRight) - translateX) / scale;
    const roundedBottom = (Math.floor(transformedBottom) - translateY) / scale;

    return {
      x: roundedLeft,
      y: roundedTop,
      width: roundedRight - roundedLeft,
      height: roundedBottom - roundedTop,
    };
  }

  // Prevents artifact between tiles.
  private drawHandwritingTilesWithAdjustment(
    context: CanvasRenderingContext2D,
    layerID: number,
    offsetX: number,
    offsetY: number,
  ) {
    const { data } = this.props;
    const { currentImageTiling, imageOffsetX, imageOffsetY } = data.layers[layerID];

    currentImageTiling.forEach((row, column, id) => {
      const tile = this.getImage(id);
      const adjustedRect = this.adjustTileRect({
        x: column * imageTileSize + imageOffsetX + offsetX,
        y: row * imageTileSize + imageOffsetY + offsetY,
        width: tile.width,
        height: tile.height,
      });

      context.drawImage(
        tile,
        adjustedRect.x,
        adjustedRect.y,
        adjustedRect.width,
        adjustedRect.height,
      );
    });
  }

  private paintHandwriting(
    context: CanvasRenderingContext2D,
    layerID: number,
    offsetX: number = 0,
    offsetY: number = 0,
  ) {
    if (
      !this.isHandwritingVisible() ||
      !this.isLayerVisible(layerID) ||
      this.getShapePaintMode(layerID) === PaintMode.Folded
    ) {
      return;
    }

    context.save();
    this.clipShapeRegion(context, layerID, this.getShapeTransformDelta(layerID));
    this.drawHandwritingTilesWithAdjustment(context, layerID, offsetX, offsetY);
    context.restore();
  }

  private paintObjectSelectionMark(context: CanvasRenderingContext2D, layerID: number) {
    const delta = this.getShapeTransformDelta(layerID);
    const shape = this.getShapeHandler(layerID);
    shape.paintSelectionFrame(context, delta, this.props.scale);
    shape.paintSelectionMark(context, delta, this.props.scale);
  }

  private paintLayer(context: CanvasRenderingContext2D, layerID: number) {
    this.paintShape(context, layerID);
    this.paintHandwriting(context, layerID);
  }

  private setHandstrokeStyle(context: CanvasRenderingContext2D, mode: DrawingMode) {
    switch (mode) {
      case DrawingMode.Handwriting: {
        const { alpha, lineColor, lineType, lineCaps, lineWidth } = this.currentDrawingAttr();
        context.globalAlpha = alpha;
        context.strokeStyle = lineColor;
        context.lineCap = lineCaps[lineType];
        setLineDash(context, lineType, lineWidth);
        break;
      }

      case DrawingMode.VerticalStraightLineHandWriting:
      case DrawingMode.HorizontalStraightLineHandWriting: {
        const { alpha, lineColor, lineType, lineWidth } = this.currentDrawingAttr();
        context.globalAlpha = alpha;
        context.strokeStyle = lineColor;
        // 水平/垂直の手書き線は実線/破線に関わらず常に角を丸くしないようにする
        context.lineCap = 'butt';
        setLineDash(context, lineType, lineWidth);
        break;
      }

      case DrawingMode.Handerasing:
        context.globalCompositeOperation = 'destination-out';
        context.globalAlpha = 1;
        context.strokeStyle = '#000';
        context.lineCap = 'round';
        break;

      default:
        throw new Error('Illegal argument.');
    }

    context.lineWidth = this.currentDrawingAttr().lineWidth;
  }

  private repaint() {
    const { props, visibleCanvas } = this;
    if (visibleCanvas === null) {
      return;
    }

    const context = visibleCanvas.getContext('2d');
    if (context === null) {
      throw new Error('Failed to get context.');
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, visibleCanvas.width, visibleCanvas.height);

    let ordinal;
    const { scale, data } = props;
    if (!isReadyToPaint(data, this.props.pictureStorage)) {
      return;
    }

    context.setTransform(scale, 0, 0, scale, props.translateX, props.translateY);

    const { layerOrder } = data;
    if (!this.isLayerVisible(data.activeLayer)) {
      this.resetActiveLayer();
    }

    const { activeLayer } = data;
    for (ordinal = 0; layerOrder[ordinal] !== activeLayer; ordinal += 1) {
      this.paintLayer(context, layerOrder[ordinal]);
    }

    this.paintShape(context, activeLayer);

    const { work } = this.state;
    switch (work.mode) {
      case DrawingMode.Selection:
        // Rounding is to prevent blur artifact made by fractional number.
        // cf. moveLayer, strokeHandwriting
        this.paintHandwriting(
          context,
          activeLayer,
          Math.round(work.offset.x),
          Math.round(work.offset.y),
        );
        break;

      case DrawingMode.VerticalStraightLineHandWriting:
      case DrawingMode.HorizontalStraightLineHandWriting:
      case DrawingMode.Handwriting:
      case DrawingMode.Handerasing: {
        const { currentImageTiling, imageOffsetX, imageOffsetY } = data.layers[activeLayer];

        this.paintHandwritingPointer(context, work);

        context.save();
        this.clipShapeRegion(context, activeLayer, this.getShapeTransformDelta(activeLayer));

        /**
         * Drawing per tile since transferring canvas as image directly on context with specifying
         * source rectangle causes memory leak on WebKit. Annoying.
         * cf. https://bugs.webkit.org/show_bug.cgi?id=134355
         *
         * In order to avoid popping, the adjustment against the artifact between tiles done by
         * drawHandwritingTilesWithAdjustment must be reproduced.
         */

        work.modifiedTiles.forEach((row, column, tile) => {
          const adjustedRect = this.adjustTileRect({
            x: column * imageTileSize + imageOffsetX,
            y: row * imageTileSize + imageOffsetY,
            width: imageTileSize,
            height: imageTileSize,
          });

          context.drawImage(
            tile,
            adjustedRect.x,
            adjustedRect.y,
            adjustedRect.width,
            adjustedRect.height,
          );
        });

        currentImageTiling.forEach((row, column, id) => {
          if (work.modifiedTiles.has(row, column)) {
            return;
          }

          const adjustedRect = this.adjustTileRect({
            x: column * imageTileSize + imageOffsetX,
            y: row * imageTileSize + imageOffsetY,
            width: imageTileSize,
            height: imageTileSize,
          });

          context.drawImage(
            this.getImage(id),
            adjustedRect.x,
            adjustedRect.y,
            adjustedRect.width,
            adjustedRect.height,
          );
        });

        context.restore();
        break;
      }

      default:
        this.paintHandwriting(context, activeLayer);
        break;
    }

    for (ordinal += 1; ordinal < layerOrder.length; ordinal += 1) {
      this.paintLayer(context, layerOrder[ordinal]);
    }

    switch (work.mode) {
      case DrawingMode.RectDrawing:
      case DrawingMode.StickyDrawing: {
        const {
          fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        } = getVisibleDrawingAttr(this.currentDrawingAttr());
        const rect = getRectFromPoints(work.start, work.end);
        drawRect(
          context, rect,
          fillColor, lineColor,
          lineWidth, lineType,
          lineCaps, alpha, isFilled,
          work.mode === DrawingMode.StickyDrawing,
        );
        break;
      }

      case DrawingMode.BalloonDrawing: {
        const {
          fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        } = getVisibleDrawingAttr(this.currentDrawingAttr());
        const rect = getRectFromPoints(work.start, work.end);
        const arrowPoint = getDefaultBalloonArrow(rect);
        drawBalloon(
          context, rect, fillColor, lineColor, lineWidth, lineType, lineCaps,
          alpha, isFilled, arrowPoint,
        );
        break;
      }

      case DrawingMode.SquareDrawing: {
        const {
          fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        } = this.currentDrawingAttr();
        const rect = getSquareRectFromPoints(work.start, work.end);
        drawRect(
          context, rect,
          fillColor, lineColor,
          lineWidth, lineType,
          lineCaps, alpha, isFilled, false,
        );
        break;
      }

      case DrawingMode.OvalDrawing: {
        const {
          fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        } = this.currentDrawingAttr();
        const rect = getRectFromPoints(work.start, work.end);
        drawOval(
          context, rect, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        );
        break;
      }

      case DrawingMode.CircleDrawing: {
        const {
          fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        } = this.currentDrawingAttr();
        const rect = getSquareRectFromPoints(work.start, work.end);
        drawOval(
          context, rect, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        );
        break;
      }

      case DrawingMode.TriangleDrawing: {
        const {
          fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        } = this.currentDrawingAttr();
        const rect = getRectFromPoints(work.start, work.end);
        drawTriangle(
          context, rect, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        );
        break;
      }

      case DrawingMode.EquilateralTriangleDrawing: {
        const {
          fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        } = this.currentDrawingAttr();
        const rect = getEquilateralTriangleRectFromPoints(work.start, work.end);
        drawTriangle(
          context, rect, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
        );
        break;
      }

      case DrawingMode.StraightLineDrawing: {
        const { lineColor, lineWidth, lineType, lineCaps, alpha } = this.currentDrawingAttr();
        const { begin, end } = work;
        drawLine(context, { begin, end }, lineColor, lineWidth, lineType, lineCaps, alpha);
        break;
      }

      case DrawingMode.AxisAlignedStraightLineDrawing: {
        const { lineColor, lineWidth, lineType, lineCaps, alpha } = this.currentDrawingAttr();
        const { begin, end } = work;
        drawLine(
          context, getAxisAlignedLineFromPoints(begin, end), lineColor, lineWidth,
          lineType, lineCaps, alpha,
        );
        break;
      }

      case DrawingMode.LearningPenRect: {
        const { lineColor, lineWidth, lineType, lineCaps } = this.currentDrawingAttr();
        const { start, end } = work;
        const rect = getLearningPenRect(start, end, lineWidth);
        // 暗記ペンは書き込みが確定するまでは半透明のため、alpha値を0.5にする
        drawRect(
          context, rect, lineColor, lineColor, 0, lineType, lineCaps, 0.5, true, false,
        );
        break;
      }

      case DrawingMode.MultiVertexDrawing: {
        const { lineColor, lineWidth, lineType, lineCaps, alpha } = this.currentDrawingAttr();
        const { vertices, currentPos } = work;
        if (lineColor === 'transparent') {
          drawTemporaryPolyline(context, vertices.concat(currentPos), this.props.scale);
        } else {
          drawPolyline(
            context, vertices.concat(currentPos), lineColor, lineWidth, lineType, lineCaps, alpha,
          );
        }
        for (const vertex of vertices) {
          drawSelectionMark(context, vertex, this.props.scale);
        }
        break;
      }

      case DrawingMode.Stamp: {
        const { stamp } = this.currentDrawingAttr();
        if (!stamp || stamp === null) {
          throw new Error('image not found.');
        }
        const rect = getStampRectFromSizeAndPoints(stamp.width,
                                                   stamp.height,
                                                   work.start,
                                                   work.end);
        const handleImageLoad = (state: StampImageState) => {
          drawStamp(context, rect, state.entry);
        };

        const imageState = this.getStampImageState(stamp.id);

        if (imageState.loaded) {
          const rect = getStampRectFromSizeAndPoints(stamp.width,
                                                     stamp.height,
                                                     work.start,
                                                     work.end);
          drawStamp(context, rect, imageState.entry);
        } else {
          if (!imageState.loadPromise) {
            throw new Error('Image load task is not work.');
          }
          imageState.loadPromise.then(handleImageLoad);
        }
        break;
      }

      default:
        break;
    }

    switch (props.toolState.mode) {
      case DrawingMode.Selection:
      case DrawingMode.Handwriting:
      case DrawingMode.Handerasing:
        this.paintObjectSelectionMark(context, activeLayer);
        break;

      default:
        break;
    }
  }

  private isPointInAnyShape(pos: Point): boolean {
    const layerID = this.getShapeLayerIdAtPoint(pos);
    return layerID !== null && layerID !== bottomLayerID;
  }

  private isNoteShapeLayer(layerID: number): boolean {
    const { props } = this;
    const { shape } = props.data.layers[layerID];
    return shape.kind === ShapeKind.Sticky || shape.kind === ShapeKind.Balloon;
  }

  private isNoteShapeHandler(shape: ShapeHandler): shape is NoteShapeHandler {
    return shape instanceof NoteShapeHandler;
  }

  private getShapeLayerIdAtPoint(pos: Point): number | null {
    const { props } = this;
    const context = this.hitTestingCanvas.getContext('2d');
    if (context === null) {
      throw new Error('Failed to get context.');
    }

    const { data } = props;
    const { layerOrder } = data;
    for (let ordinal = layerOrder.length - 1; ordinal >= 0; ordinal -= 1) {
      const shape = this.getShapeHandler(layerOrder[ordinal]);
      if (shape.isPointInShape(pos, context)) {
        return layerOrder[ordinal];
      }
    }
    return null;
  }

  private removeLayerAt(pos: Point) {
    const layerID = this.getShapeLayerIdAtPoint(pos);
    if (layerID !== null && layerID !== bottomLayerID) {
      this.props.onDrawingAction(removeLayer(layerID));
    }
  }

  private getCursorValue(): string {
    const { work } = this.state;
    switch (work.mode) {
      case DrawingMode.ObjectRemoval:
        if (work.hover) {
          return 'crosshair';
        }
      case DrawingMode.Handwriting:
      case DrawingMode.Handerasing: {
        return this.capturing ? 'none' : 'auto';
      }
      default:
        return 'auto';
    }
  }

  private createNoteClickableArea() {
    if (this.props.toolState.mode !== DrawingMode.Disabled) {
      return null;
    }

    const { layerOrder } = this.props.data;
    const clickableAreas = [];
    for (let ordinal = 0; ordinal < layerOrder.length; ordinal += 1) {
      const layerID = layerOrder[ordinal];
      const shapeHandler = this.getNoteShapeHandler(layerID);
      if (shapeHandler !== null) {
        const { scale, translateX, translateY, onDrawingAction } = this.props;
        const rect = shapeHandler.isFolded() ? shapeHandler.getFoldedRect() : shapeHandler.shape;
        clickableAreas.push(<ClickableArea
          key={`NoteClickableArea${ordinal}`}
          rect={rect}
          scale={scale}
          translateX={translateX}
          translateY={translateY}
          onClick={() => {
            onDrawingAction(foldNote(layerID, !shapeHandler.isFolded()));
          }}
        />);
      }
    }

    return clickableAreas;
  }

  private createLearningPenClickableArea() {
    if (this.props.toolState.mode !== DrawingMode.Disabled) {
      return null;
    }

    const clickableAreas = [];
    const { layerOrder } = this.props.data;
    for (let ordinal = 0; ordinal < layerOrder.length; ordinal += 1) {
      const { props } = this;
      const layerID = layerOrder[ordinal];
      const { shape } = props.data.layers[layerID];
      if (shape.kind === ShapeKind.LearningPenRect) {
        const { alpha } = shape;
        const shapeHandler = new LearningPenRectShapeHandler(shape);
        const rect = shapeHandler.getEnclosingRect();
        clickableAreas.push(<ClickableArea
          key={`LearningPenClickableArea${ordinal}`}
          rect={rect}
          scale={props.scale}
          translateX={props.translateX}
          translateY={props.translateY}
          onClick={() => this.props.onDrawingAction(
            toggleTransparencyOfLearningPen(layerID, alpha === 1 ? false : true))}
        />);
      }
    }
    return clickableAreas;
  }

  private createMultiroleLinkClickableArea() {
    if (this.props.toolState.mode !== DrawingMode.Disabled) {
      return null;
    }

    const clickableAreas = [];
    const { layerOrder } = this.props.data;
    for (let ordinal = 0; ordinal < layerOrder.length; ordinal += 1) {
      const { props } = this;
      const { shape } = props.data.layers[layerOrder[ordinal]];
      if (shape.kind === ShapeKind.MultiroleLink) {
        const { x, y, multiroleLink } = shape;
        const { width, height } = this.multiroleLinkIcon;
        const rect = { x, y, width, height };
        clickableAreas.push(<ClickableArea
          key={`MultiroleLink${ordinal}`}
          title={multiroleLink.title}
          rect={rect}
          scale={props.scale}
          translateX={props.translateX}
          translateY={props.translateY}
          onClick={() => props.onMultiroleLinkClick(multiroleLink)}
        />);
      }
    }
    return clickableAreas;
  }

  private createTextBox() {
    if (!this.state.editingText) {
      return null;
    }

    const { activeLayer } = this.props.data;
    const shape = this.getNoteShapeHandler(activeLayer);
    if (shape === null) {
      return;
    }

    return <TextBox
      key={`TextBox${activeLayer}`}
      rect={shape.shape}
      scale={this.props.scale}
      translateX={this.props.translateX}
      translateY={this.props.translateY}
      font={shape.getFont()}
      lineHeight={shape.shape.lineHeight}
      padding={shape.shape.padding * textBoxDefaultPaddingSize * fundamentalSizeMultiplier}
      defaultValue={shape.shape.text}
      onDrawingAction={this.props.onDrawingAction}
      onBlur={() => this.setState({ ...this.state, editingText: false })} />;
  }

  private createPointerEventListeners() {
    if (hasPointerEvents()) {
      // Neither onPointerDown nor onPointerMove is supported. Done by componentDidMount instead.
      return {};
    }

    return {
      onMouseMove: this.handlePointerMoveDirect,
      onMouseDown: this.handlePointerDown,
      onTouchStart: this.handlePointerDown,
    };
  }

  public render() {
    const style: React.CSSProperties = {
      position: 'absolute',
      top: 0,
      left: 0,
      pointerEvents: (this.props.toolState.mode === DrawingMode.Disabled) ? 'none' : 'auto',
      cursor: this.getCursorValue(),
    };

    return [
      <canvas
        {...this.createPointerEventListeners()}
        key="DrawingCanvas"
        ref={el => this.visibleCanvas = el}
        style={style}
        width={this.props.pageAreaWidth}
        height={this.props.pageAreaHeight} />,
      this.createTextBox(),
      this.createNoteClickableArea(),
      this.createMultiroleLinkClickableArea(),
      this.createLearningPenClickableArea(),
    ];
  }

  public componentDidMount() {
    if (this.visibleCanvas !== null && hasPointerEvents()) {
      this.visibleCanvas.addEventListener('pointermove', this.handlePointerMoveDirect);
      this.visibleCanvas.addEventListener('pointerdown', this.handlePointerDown);
    }

    this.repaint();
  }

  public componentWillUnmount() {
    this.releaseCapture();

    if (this.visibleCanvas !== null && hasPointerEvents()) {
      this.visibleCanvas.removeEventListener('pointermove', this.handlePointerMoveDirect);
      this.visibleCanvas.removeEventListener('pointerdown', this.handlePointerDown);
    }
  }

  public componentDidUpdate(prevProps: Readonly<DrawingCanvasPropsType>) {
    this.replenishIntermediateTilesIfNeeded(this.props, prevProps);
    this.repaint();
  }

  public static getDerivedStateFromProps(
    props: Readonly<DrawingCanvasProps>,
    state: DrawingCanvasState,
  ) {
    if (
      state.work.mode === DrawingMode.MultiVertexDrawing
      && props.toolState.mode !== DrawingMode.MultiVertexDrawing
    ) {
      return { work: { mode: DrawingMode.Disabled } };
    }

    return null;
  }
}
